// catalogue.ts — the human-readable description of every option in the game's SRDebugger panel.
//
// ============================ KEEP THIS IN SYNC ============================
// This file mirrors, by hand, the game's options in the Unity project:
//
//   Assets/Scripts/TWGS.General/<Category>Options.cs     (the values, defaults, panel labels + categories)
//   Assets/Scripts/TWGS.General/SRDebugOptions/SRDebug*.cs  (the rows that are not an option value)
//
// When you add, rename, retune or delete an option there, update it HERE in the same commit. `label` must
// match the SRDebugger [DisplayName] (or the property name when there isn't one) and `category` must match
// its [Category], otherwise someone reading this page won't find the row they're looking at on the device.
// `level` must match its [SRLevel] (leave it out for Crucial, which is what an untagged row counts as).
// ===========================================================================
export const SR_CATEGORIES = [
    // -----------------------------------------------------------------------
    {
        id: "general",
        label: "General",
        blurb: "Build identity and the level you're on. The quickest way to jump around while testing.",
        options: [
            {
                label: "Build Version", code: "Application.version", kind: "readonly",
                summary: "The version of the build you're holding. Read-only.",
                detail: "Quote this in any bug report — it's the only way to know which build a tester was on.",
            },
            {
                label: "Current Level", code: "CurrentLevel", kind: "int", default: "1",
                summary: "Which authored level you're on. Type a number to jump straight to it.",
                detail: "1-based, stored in the player's progression data (the same record a future backend would " +
                    "sync), and it goes up by one every time you win — until the last authored level. Winning " +
                    "that moves it one past the end, where it stays: from then on wins count in Current Loop. " +
                    "It can't be set higher than one past the end. Feature unlocks follow this number, not the loop. " +
                    "It also decides the board size, so changing it changes the shape of the level too.",
                tip: "Set it, then restart the app (or fail/win out) so the new level is actually loaded. " +
                    "Data > Reset Data clears it along with the rest of the progression.",
            },
            {
                label: "Current Loop", code: "CurrentLoop", kind: "int", default: "0",
                summary: "How many levels you've won past the last authored level. Type a number to set it.",
                detail: "0 until the authored set is done, stored in the player's progression data next to Current " +
                    "Level. Once Current Level is one past the end, this picks which re-dealt board plays. The " +
                    "level number you see (Play button, in-game plate, fail popup, analytics) is Current Level " +
                    "plus this.",
                tip: "To test the loop, set Current Level one past the last authored level, then set this. " +
                    "Data > Reset Data puts it back to 0 along with the rest of the progression.",
            },
            {
                label: "TargetFrameRate", code: "Application.targetFrameRate", kind: "int", level: "moderate", default: "60",
                summary: "How many frames per second the game aims for.",
                detail: "The game turns vSync off on purpose so this number is the one in charge. On a 120Hz Android " +
                    "phone, leaving vSync on made the frame rate jump between 120/60/40/30, which reads as stutter.",
                tip: "Drop it to 30 to feel how the animations hold up on a weak device.",
            },
            {
                label: "Slow Mode (0.1x)", code: "SRDebugCoreOptions.SlowMode", kind: "bool", default: "off",
                summary: "One-tap slow motion for watching an animation or a physics moment closely.",
                detail: "Runs the whole game at a tenth speed AND divides the physics timestep by ten to match, " +
                    "so the simulation keeps its usual number of steps per rendered second and stays smooth — " +
                    "a bare time-scale drop makes physics visibly chunky. Turning it off restores both.",
                tip: "Not saved: a restart always comes back at normal speed.",
            },
            {
                label: "RestartApp", code: "AppManager.Restart", kind: "action",
                summary: "Reboots the game from the very start, without closing it.",
                detail: "The fastest way to apply anything that's only read once at boot.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "ads",
        label: "Ads",
        blurb: "The ad switches and AppLovin's own test tooling, from core. Shown by default. Ads only run on " +
            "dev builds with the MAX ad units filled (Android today), and none of this exists in a prod build.",
        options: [
            {
                label: "Show Mediation Debugger", code: "AdvertisingManager.ShowMediationDebugger", kind: "action",
                summary: "Opens AppLovin's built-in overlay: per-network status, test modes, test ads.",
                detail: "The overlay is AppLovin's, not ours — use it to verify each mediated network is " +
                    "integrated and to fire test ads per ad unit.",
                warn: "Does nothing until the MAX SDK finishes initialising, a few seconds after boot.",
            },
            {
                label: "Toggle Ad Waterfall Overlay", code: "AdvertisingManager.ToggleAdWaterfallOverlay", kind: "action",
                summary: "Shows or hides an on-screen inspector of the interstitial and rewarded waterfalls.",
                detail: "Per format: the mode, the floored track's state, the session (last fill, re-descents, skipped " +
                    "rungs, age), the floorless ad, every rung, and the recent ad log. Works on simulated ads too.",
            },
            {
                label: "Interstitial Ads Active", code: "CoreAdsOptions.InterstitialAdsActive", kind: "bool", default: "on",
                summary: "Off stops interstitials, so you can play without them interrupting.",
                detail: "A dev-build switch: only TWIST_DEV builds show the row and honour it. Rewarded ads and banners " +
                    "are not affected. Kept on the device until you turn it back on.",
                warn: "The game then treats this player as ad-free: the No Ads button and the No Ads popup go too.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "haptics",
        label: "Haptics",
        blurb: "The buzz in your hand. Every value is 0 to 1. Intensity is how hard the tap is; sharpness is how " +
            "it feels (0 = a dull thud, 1 = a crisp click) and only iPhone can do sharpness at all.",
        options: [
            {
                label: "Haptics Enabled", code: "HapticsEnabled", kind: "bool", default: "on",
                summary: "The master switch. Off means the phone never buzzes.",
                detail: "Saved with the player's settings, not as a debug value — the same switch the game's own options screen would flip.",
            },
            {
                label: "Sound Enabled", code: "SoundEnabled", kind: "bool", default: "on",
                summary: "The master switch for audio. Off silences the game.",
                detail: "Lives under the panel's own 'Audio' heading rather than here, and like Haptics Enabled it's saved with the player's settings.",
            },
            {
                label: "Egg Tick Spacing", code: "EggDropTickSpacing", kind: "float", default: "0.04", unit: "seconds",
                summary: "The gap between the nine little taps you feel when a box dumps its eggs.",
                detail: "The eggs drop faster than the phone can be asked to buzz one at a time, so the whole pattern " +
                    "of nine taps is scheduled up front as a single vibration. This is the spacing inside it.",
                tip: "Bigger = a slow drum roll. Smaller = one long brrrp.",
            },
            {
                label: "Egg Tick Intensity", code: "EggDropTickIntensity", kind: "float", default: "1", unit: "0–1",
                summary: "How hard each of those nine taps hits.",
                tip: "0 switches the egg-drop buzz off without touching any other haptic.",
            },
            {
                label: "Egg Tick Sharpness (iOS)", code: "EggDropTickSharpness", kind: "float", default: "0.5", unit: "0–1",
                summary: "How crisp each tap feels, on iPhone only.",
                warn: "Android ignores this completely — don't tune it on an Android device and expect a difference.",
            },
            {
                label: "TestHaptic", code: "HapticFeedback(Medium)", kind: "action",
                summary: "Buzzes once, right now, so you can check haptics work at all.",
            },
            {
                label: "TestEggTickPattern", code: "HapticPattern(9, …)", kind: "action",
                summary: "Plays the full nine-tap egg-drop pattern using the three values above.",
                tip: "This is the one to hammer while tuning spacing/intensity — no need to find a box and tap it.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "gameplay",
        label: "Gameplay",
        blurb: "Buttons that end the level for you (so you don't have to actually play it), plus a rig " +
            "for the end-game rescue strip.",
        options: [
            {
                label: "DebugWin", code: "DebugWin", kind: "action",
                summary: "Instantly win the level and show the win flow.",
            },
            {
                label: "DebugFail", code: "DebugFail", kind: "action",
                summary: "Instantly show the Level Failed popup.",
                detail: "Quicker than earning a real fail — the game's own fail fires only on a full deadlock " +
                    "(belt completely full, everything settled, and no riding ball matches any open slice).",
            },
            {
                label: "Ego Collect", code: "EgoCollect", kind: "action", level: "moderate",
                summary: "Run the end-game revive's collect right now, without failing first.",
                detail: "What the paid continue does to the board: the belt's biggest same-colour group " +
                    "(up to 9 balls) plus every loose ball (falling, jammed, or waiting in the holder) " +
                    "fly into rescue boxes on the strip below the board — one colour per box, topping " +
                    "up part-full rescue boxes before opening new cells from the middle outward. " +
                    "Tapping a rescue box throws its balls back into play like any other box.",
            },
            {
                label: "Tap To Skip Fill Speed", code: "TapToSkipFillSpeed", kind: "float", level: "moderate", default: "1", unit: "x",
                summary: "How fast Tap To Skip's FillSlices burst plays (1 = as tuned, 0.75 = a quarter slower).",
                detail: "A game-clock factor held only while the burst runs: the screws' flights, the slices clearing " +
                    "and stepping up, and the belt all change speed together, so each group still lands just as " +
                    "its slice arrives. Back to normal the moment the burst ends — the win screen always plays " +
                    "at normal speed. Multiplies with Conveyor > Endgame Speed Up when that's on. Clamped to " +
                    "0.1 – 3.",
            },
            {
                label: "Fail Warning", code: "FailWarningEnabled", kind: "bool", level: "moderate", default: "on",
                summary: "Pulses an orange-red glow around the booster buttons (and swells them) when the next move may lose the level.",
                detail: "From level 11 on (the same levels as Tap To Skip), it comes on when ALL of these hold: " +
                    "the belt has 1 to 9 free cells; every loose ball is " +
                    "already riding the belt (nothing falling, parked in the holder or hopping into a slice); " +
                    "and no ball on the belt has a slice it could go into. With 4 to 9 free cells there must " +
                    "also be no tappable box whose colour matches a top slice — with only 1 to 3 free a box " +
                    "won't save you, so boxes are ignored. Once that has held for Fail Warning Delay it fades " +
                    "in, and it fades out the moment any of it stops being true. Never shows while a booster " +
                    "runs, once the win is certain, or after the level is decided. A locked booster's " +
                    "button doesn't glow.",
                tip: "Off hides it entirely — handy when you want to judge a level without the nudge.",
            },
            {
                label: "Fail Warning Delay", code: "FailWarningDelay", kind: "float", level: "moderate", default: "1", unit: "seconds",
                summary: "How long the danger has to last before the fail warning glows start.",
                detail: "Counted on the game clock, so a pause popup holds it. 0 shows the glows the moment the conditions are met.",
            },
            {
                label: "Dot Colour", code: "WheelDotColour", kind: "enum", default: "Blossom",
                figure: "ring",
                summary: "Which colour the warning dots and their glow use: the old Purple, the frame's Cream or a Blossom pink.",
            },
            {
                label: "Dot Glow", code: "WheelDotGlow", kind: "bool", default: "off",
                figure: "ring",
                summary: "Whether the warning dots breathe a glow in and out.",
            },
            {
                label: "Fail Warning State", code: "FailWarningState", kind: "readonly", level: "moderate",
                summary: "Why the fail warning is or isn't showing right now. Read-only.",
                detail: "The first condition that's failing (e.g. \"free cells = 12\", \"belt ball has a slice\", " +
                    "\"ball off the belt\" with the ball's state), or \"DANGER\" when they all hold.",
            },
            {
                label: "Tap To Skip", code: "TapToSkipMode", kind: "enum", level: "moderate", default: "ShowWinScreen",
                summary: "What tapping the screen does once the level can no longer be lost.",
                detail: "From level 11 on, once the win is certain (no box or tunnel left to hand out a ball, and " +
                    "everything still dropping fits on the belt), a pulsing \"Tap to Skip\" " +
                    "sits at the bottom of the screen. A tap anywhere except a button skips the wait. " +
                    "ShowWinScreen: the win screen comes up right away, and the belt keeps running underneath " +
                    "at normal speed, filling the slices by itself. FillSlices (\"fly home\"): every ball still on " +
                    "the belt, falling or waiting in the holder is given a slice of its colour, and the slices " +
                    "fill one at a time: the outer ring first, round with the belt, then each row behind. A " +
                    "slice's screws crouch and lift together on low arcs, each landing throwing a small spray, " +
                    "and the next group lifts while the previous one is still flying; a group behind lands once " +
                    "its slice has stepped up to the outer ring. A slice counts full a beat after its last " +
                    "touchdown, so the queue never waits on the screws. Balls waiting their turn keep riding the " +
                    "belt. Tap To Skip Fill Speed sets how fast the whole burst plays. " +
                    "The win screen follows once the last slice clears. Off: no prompt and no skip.",
                tip: "If a tunnel refill or a dropped-back ball makes the win uncertain again, the prompt goes away with it.",
            },
            {
                label: "Drop Slice Screw", code: "DropSliceScrew", kind: "action", level: "moderate",
                summary: "Drop one live ball from the middle of the board, coloured like one of the top slices.",
                detail: "A test faucet for the funnel / holder / belt / slice chain without tapping boxes: the " +
                    "ball is built exactly like a box egg (same prefab and physics material), given the " +
                    "colour of a randomly picked ACTIVE slice — so it always has somewhere to go — and " +
                    "released into free fall at the board's centre. Ignores the in-play cap.",
                tip: "Does nothing once every slice is gone (a dropped ball could never leave the belt).",
            },
            {
                label: "Save Wall PNG", code: "SaveWallPng", kind: "action", level: "moderate",
                summary: "Saves the CURRENT level's walls as one transparent PNG at the game's native 1302x2818.",
                detail: "Every wall island — the outer frame and any enclosed pockets — photographed through the " +
                    "game camera's own framing onto a transparent background, so the image drops onto a " +
                    "full-size screen with the walls exactly where the player sees them. The wall is rebuilt " +
                    "twice while it runs (once at export resolution so the picture is crisp, once to put the " +
                    "screen-resolution one back), which looks like a brief flicker.",
                tip: "In the Unity editor the file lands in a WallExports folder beside the project; on a " +
                    "device it goes to the app's own storage. The full path is printed to the log either way.",
                warn: "It exports the board you are PLAYING — run it from a level, not the menu. The same " +
                    "export is the Content Editor's \"Export walls\" button.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "featureScreens",
        label: "Feature Screens",
        blurb: "Flip through every feature's \"New Feature!\" screen without playing to its level.",
        options: [
            {
                label: "Next Feature Screen", code: "NextFeatureScreen", kind: "action",
                summary: "Opens the \"New Feature!\" screen for the next feature in the catalogue (in unlock order).",
                detail: "The same screen a player sees on entering a feature's level, with the game paused under it " +
                    "and the feature's own animation playing. Pressing it again while a screen is up closes that " +
                    "one and opens the next; after the last feature it wraps to the first.",
                warn: "Works inside a level only — the screen lives on the gameplay scene.",
            },
            {
                label: "Previous Feature Screen", code: "PreviousFeatureScreen", kind: "action",
                summary: "The same, one feature back.",
            },
            {
                label: "Clear Seen Feature Screens", code: "ClearSeenFeatureScreens", kind: "action",
                summary: "Forgets which \"New Feature!\" screens this player has seen, so each shows again on its level.",
                detail: "A player sees each feature's screen once in the app's lifetime: it is recorded in the " +
                    "SeenFeatureUnlocks storage object as it opens, and never shows for that feature again — " +
                    "not on a retry, a later visit or a new session. This empties that record.",
                tip: "Takes effect on the next entry to a feature's level; a level already open is not affected.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "inventory",
        label: "Inventory",
        blurb: "Testing shortcuts for the inventory (booster stock and coins), so you don't have to earn or buy them.",
        options: [
            {
                label: "Add 3 To All Boosters", code: "AddThreeToAllBoosters", kind: "action",
                summary: "Grants +3 of every booster (Magnet, Shuffle, Cannon) to the inventory.",
                detail: "Adds to the live UserInventory via BoosterOps.AddBooster (reason \"sr_debug\"), the same " +
                    "counted Items bag the boosters spend from. The gain persists like any other stock change.",
            },
            {
                label: "Add 1000 Coins", code: "AddThousandCoins", kind: "action",
                summary: "Grants +1000 coins to the inventory.",
                detail: "Adds to the live UserInventory via CoinOps.AddCoin (reason \"sr_debug\") — the balance the " +
                    "fail-continue offers spend from (900/1900/2900 ladder). Tap repeatedly for more.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "ball-physics",
        label: "Screw Physics",
        blurb: "What the balls are made of, physically. Both are stamped onto a copy of the ball material when a " +
            "level starts, so a change here needs a level restart — and the copy means fiddling with them never " +
            "edits the material asset itself.",
        options: [
            {
                label: "Gravity (down, next level)", code: "Gravity", kind: "float", default: "50",
                summary: "How hard everything is pulled down.",
                detail: "Written as a positive number — 10 here means gravity points down with a strength of 10. " +
                    "It's the whole world's gravity, not just the balls'. The spawn throw itself is animated, " +
                    "not physical, so this takes over from the moment a ball lands from its throw.",
                tip: "Lower it to watch the drop in slow motion; the balls fall lazily but everything else keeps its normal timing.",
            },
            {
                label: "Mass (next level)", code: "ScrewMass", kind: "float", default: "1",
                summary: "How heavy a ball is.",
                detail: "This does NOT make them fall faster — gravity pulls everything at the same rate, heavy " +
                    "or light. What it changes is how a ball answers being pushed. Heavy balls carry more " +
                    "momentum into the pile and shrug off the little deflections that used to leave them " +
                    "perched on the funnel.",
                warn: "The jam breaker's nudge is an impulse, so it's divided by this — double the mass and every nudge moves the ball half as far.",
            },
            {
                label: "Velocity Iterations (next level)", code: "VelocityIterations", kind: "int", default: "10",
                summary: "How many passes the physics solver spends getting collisions to behave each step.",
                detail: "Each pass is another go at making the touching balls agree on how fast they should be " +
                    "moving. Too few and a stack of balls never settles — it shivers, sinks into itself, and " +
                    "generally behaves like it's made of jelly.",
                tip: "Unity's default is 8. Raising it is the first thing to try when a resting pile looks unstable.",
            },
            {
                label: "Position Iterations (next level)", code: "PositionIterations", kind: "int", default: "4",
                summary: "How many passes it spends pushing apart things that ended up overlapping.",
                detail: "Separate from the one above: that fixes speeds, this fixes overlaps. Raise it when balls visibly sink into each other or into the walls under the weight of the pile.",
                tip: "Unity's default is 3.",
                warn: "Both of these cost CPU in direct proportion, and they slow down the WHOLE simulation, not just the balls. Check the frame rate on a real device, not in the editor.",
            },
            {
                label: "Continuous Collision Above Speed (0 = always)", code: "ScrewContinuousCollisionSpeed", kind: "float", default: "18", unit: "units/sec",
                summary: "Above this speed a loose ball uses the expensive, never-misses collision check; below it, the cheap one.",
                detail: "The careful check sweeps the ball's whole path each step so a fast ball can't skip through a " +
                    "thin wall. A pile of slow, touching balls doesn't need it but used to pay for it on every " +
                    "contact. The cheap check is only unsafe once a ball moves more than its own radius in one " +
                    "physics step — about 23 units/sec — so 18 keeps a margin. It is applied live, every step.",
                tip: "If a ball ever passes through a wall, lower this (or set 0 to always use the careful check).",
            },
            {
                label: "Friction Combine (next level)", code: "FrictionCombine", kind: "int", default: "Maximum",
                summary: "How the ball's grip and the wall's grip are turned into the one number the physics uses.",
                detail: "Maximum takes the larger of the two, so the friction values below mean what they say. " +
                    "Multiply takes their product, which is what the material originally shipped with — and " +
                    "why a 0.4 ball on a 0.4 wall was really running 0.16, a third of what the number looked " +
                    "like. The same mode is written to both materials, so they can never disagree.",
            },
            {
                label: "Friction (next level)", code: "ScrewFriction", kind: "float", default: "0",
                summary: "How much the balls grip whatever they touch — including each other.",
                detail: "Zero on purpose. Balls with no grip can't hold ONE ANOTHER up, so a pile can't lock into " +
                    "an arch over the funnel mouth — there's nothing for a keystone to hang from. The walls " +
                    "still provide grip against the slope (see Wall Friction), because the larger of the two " +
                    "values wins.",
                tip: "A frictionless ball never spins on its own either, which is why the roll you see is faked from the ball's height.",
                warn: "Turn this up and the pile starts bridging over the mouth and stopping the level.",
            },
            {
                label: "Bounciness (next level)", code: "ScrewBounciness", kind: "float", default: "0.05",
                summary: "How much of an impact a ball gives back.",
                detail: "0 is a dead stop; 1 bounces back to the height it fell from. Kept tiny — just enough " +
                    "life on impact — because the pile has to settle into the holder rather than keep hopping.",
            },
            {
                label: "Wall Friction (next level)", code: "WallFriction", kind: "float", default: "0.2",
                summary: "Grip of the funnel walls the falling balls slide down.",
                detail: "The larger of the two values wins, so with the balls at 0 this one alone decides how " +
                    "much they drag on the funnel.",
                warn: "There's a ceiling: the funnel slopes at 13 degrees, so past about 0.23 a pile stops sliding and perches on the wall instead of feeding the holder. 0.2 sits just under it.",
            },
            {
                label: "Wall Bounciness (next level)", code: "WallBounciness", kind: "float", default: "0",
                summary: "How much the funnel walls throw a ball back.",
                detail: "Bounciness takes the LARGER of the two surfaces, not the product — so raising this makes the walls bouncy no matter what the ball is set to.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "ball-scale",
        label: "Screw Scale",
        blurb: "A ball has one fixed size per stage of its life, and every move between stages tweens from the size " +
            "it currently is to the next one. These four are those sizes.\n\n" +
            "They are read ONCE when a level starts, so changing one here does nothing until you restart the " +
            "level. That's deliberate: a ball's transitions start from wherever it already is, so a mid-level " +
            "change would leave the balls that already passed a stage at the old size and everything else at the " +
            "new one, with nothing to reconcile them.",
        options: [
            {
                label: "In Box (next level)", code: "ScrewInBoxScale", kind: "float", default: "0.56",
                summary: "Packed in a box, before it drops.",
                detail: "Nine of these have to fit in one cell, which is why they're so much smaller than a loose ball.",
            },
            {
                label: "Active (next level)", code: "ScrewActiveScale", kind: "float", default: "0.87",
                summary: "Loose on the board — falling, or held above the holder.",
                tip: "The reference size: read the other three as fractions of this one.",
            },
            {
                label: "On Wheel (next level)", code: "ScrewWheelScale", kind: "float", default: "0.9844",
                summary: "Riding a hole on the belt.",
                detail: "Bigger than loose, so the belt reads as the layer in front. Riding balls have physics off, so " +
                    "the larger circle never shoves anything — but it is live during the scripted DROP into the " +
                    "slot, where loose balls bounce off it, and it sets the clearance the entrance claim box insets by.",
                warn: "0.9844 is the old 0.92 with the 1.07 art multiplier folded into it, so the ball draws exactly " +
                    "as it did while the visual row went back to 1. The COLLIDER came along for the ride: it is ~7% " +
                    "wider than before in those two places.",
            },
            {
                label: "In Slice (next level)", code: "ScrewInSliceScale", kind: "float", default: "0.884",
                summary: "Parked in a slice hole, waiting for the slice to fill.",
                detail: "0.884 is the old 0.85 with its 1.04 art multiplier folded in — same move as the belt row, and " +
                    "harmless here, because a seated ball is never simulated.",
            },
            {
                label: "Slice Jump Peak (next level)", code: "ScrewSliceJumpPeakScale", kind: "float", default: "1.035",
                summary: "The size a ball swells to on its way from the belt into a slice, before it seats.",
                detail: "The hop has two legs now. The flight grows the ball from its belt size to this peak and " +
                    "holds it there; the landing punch then presses it down to the in-slice size. It gives the " +
                    "arc a lift of its own, so the ball reads as rising over the rim and being pressed home " +
                    "rather than just moving across and shrinking.",
                tip: "Set it equal to In Slice for the old behaviour — a flat flight with all the resizing after touchdown.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "ball-visual-scale",
        label: "Screw Visual Scale",
        blurb: "The same four stages again, but for the ball's ART only. Each of these multiplies the Ball Scale " +
            "above and is applied to the ball's \"Visual\" child, so the ROOT keeps its size — and with it the " +
            "collider, the physics, and every position the game works out from the ball. Only what you SEE " +
            "changes.\n\n" +
            "1 means \"exactly the size the root says\", and in box, belt and slice all sit there now: the belt " +
            "and slice multipliers were folded back into their root sizes (0.92 x 1.07 → 0.9844, 0.85 x 1.04 " +
            "→ 0.884), so those balls draw unchanged with one number each instead of two. Only a LOOSE ball " +
            "still lifts its art over the root, at 1.07 — that is the stage whose collider is doing funnel " +
            "physics, so its size is the one worth leaving alone. Read once per level, like the sizes above.",
        options: [
            {
                label: "In Box (next level)", code: "ScrewInBoxVisualScale", kind: "float", default: "1",
                summary: "Art size while packed in a box.",
            },
            {
                label: "Active (next level)", code: "ScrewActiveVisualScale", kind: "float", default: "1.07",
                summary: "Art size while loose on the board.",
            },
            {
                label: "On Wheel (next level)", code: "ScrewWheelVisualScale", kind: "float", default: "1",
                summary: "Art size while riding a hole on the belt.",
            },
            {
                label: "In Slice (next level)", code: "ScrewInSliceVisualScale", kind: "float", default: "1",
                summary: "Art size while parked in a slice hole.",
            },
            {
                label: "Slice Jump Peak (next level)", code: "ScrewSliceJumpPeakVisualScale", kind: "float", default: "1",
                summary: "Art size while the ball is swelling to its jump peak.",
                detail: "Applied instantly when that leg starts, like the others — the root carries the swell itself.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "ball",
        label: "Screw",
        blurb: "How a ball behaves from the moment it's flung out of its box until it leaves the board. This is where " +
            "most of the 'feel' lives.",
        options: [
            {
                label: "MaxScrewsInPlay", code: "MaxScrewsInPlay", kind: "int", default: "54", figure: "cap",
                summary: "The most balls allowed loose on the board at once.",
                detail: "'In play' means balls that have left a box and haven't been taken by a slice yet — the ones " +
                    "bouncing around and the ones riding the conveyor. Balls still sitting in an un-opened box " +
                    "don't count (they haven't entered play), and a ball a slice has claimed doesn't count either " +
                    "(it's on its way off the board).\n\n" +
                    "Because tapping a box releases all nine of its eggs at once, a box refuses to open unless " +
                    "there are nine free slots. At 46 balls in play, taps do nothing.",
                tip: "Lower it to see the board choke sooner; raise it to test heavy physics load. A refused tap shows a 'Board Full!' toast above the finger.",
                warn: "This only blocks PLAYER taps. Connector and flood cascades still drop their boxes even if that " +
                    "pushes you over the cap — blocking them mid-cascade could stall the level.",
            },
            {
                label: "GrowMultiplier", code: "GrowMultiplier", kind: "float", default: "1.4",
                summary: "How much bigger than normal a ball puffs up as it pops out of its box.",
                warn: "Currently wired to nothing — the spawn grow goes In Box → Active over GrowDuration. Changing this does nothing.",
            },
            {
                label: "Grow Duration (frames)", code: "GrowDurationFrames", kind: "int", default: "12", unit: "frames",
                summary: "How long that puff takes — counted in frames like the spawn delays. 12 at 60fps is about 0.2s.",
            },
            {
                label: "StartColliderFactor", code: "StartColliderFactor", kind: "float", default: "0", unit: "0–1",
                summary: "How big the ball's invisible physics circle is at the instant it's born.",
                detail: "0 means it starts as a point and grows to full size along with the visual. That's deliberate: " +
                    "a ball popping out right next to another one starts with no overlap, so physics can ease them " +
                    "apart instead of violently shoving them.",
                warn: "Push this toward 1 and freshly spawned balls will kick each other across the board.",
            },
            {
                label: "Spawn Hop Height (bottom row)", code: "SpawnHopHeightBottomRow", kind: "float", default: "0.2", unit: "world units",
                summary: "How high an egg from the BOTTOM row of the box pops as it comes out.",
                detail: "There's one of these per row, and they need different numbers to look the same.\n\n" +
                    "The nine eggs land further apart than they sat in the box, so each one also has to travel " +
                    "vertically to reach its spot: the top row climbs about 0.42, the middle row goes nowhere, " +
                    "the bottom row drops about 0.42. That travel is added to the pop. Give all three the same " +
                    "height and the top row shows a big arc while the bottom row shows none — the pop is buried " +
                    "under its drop.",
                tip: "The three defaults (0.2 / 0.15 / 0.1) are chosen so all three rows arc by about the same amount on screen. 0 means no pop at all.",
            },
            {
                label: "Spawn Hop Height (middle row)", code: "SpawnHopHeightMiddleRow", kind: "float", default: "0.15", unit: "world units",
                summary: "The same, for the middle row.",
                detail: "The middle row doesn't travel vertically at all, so this value is exactly the arc you see — no more, no less.",
            },
            {
                label: "Spawn Hop Height (top row)", code: "SpawnHopHeightTopRow", kind: "float", default: "0.1", unit: "world units",
                summary: "The same, for the top row.",
                detail: "The top row already climbs about 0.42 on its way to its landing spot, so it looks like a big arc even with this set low. It usually wants the SMALLEST of the three.",
            },
            {
                label: "Spawn Spread Spacing (horizontal)", code: "SpawnSpreadSpacingHorizontal", kind: "float", default: "1", figure: "spread",
                summary: "How far apart the nine eggs land SIDE TO SIDE, measured in ball-widths.",
                detail: "1 means the columns come to rest exactly touching. Above 1 leaves a gap; below 1 makes " +
                    "them overlap.\n\n" +
                    "This is the spread that matters for the funnel: it's what stops all nine eggs arriving " +
                    "at its mouth in a single column.",
                warn: "Below 1 you're handing physics nine overlapping circles, and it will shove them apart.",
            },
            {
                label: "Spawn Spread Spacing (vertical)", code: "SpawnSpreadSpacingVertical", kind: "float", default: "0.5", figure: "spread",
                summary: "The same, for how far apart the three ROWS land.",
                detail: "1 means the rows come to rest exactly one ball apart. The rows are stacked in the " +
                    "direction the eggs are about to fall, so widening this just makes the upper rows start " +
                    "their drop from higher — gravity closes the extra gap on the way down. It changes the " +
                    "rhythm of the landing more than the shape of the pile.",
                warn: "Below 1 the rows land overlapping, and physics will shove them apart.",
            },
            {
                label: "Spawn Exit Down Speed", code: "SpawnExitDownSpeed", kind: "float", default: "2.5", unit: "world units/sec",
                summary: "How fast a ball is already falling the instant its throw ends and physics takes over.",
                detail: "The throw is scripted; the fall is physics. This is the downward speed the ball is handed " +
                    "to gravity with, one fixed number for every ball. 0 means it lands dead and gravity " +
                    "accelerates it from rest.",
                tip: "Around 2.5 it roughly matches the arc's own landing speed, so the throw flows into the fall without a visible seam.",
            },
            {
                label: "SpawnScaleEase", code: "SpawnScaleEase", kind: "enum", default: "InCubic",
                summary: "Whether a ball grows to full size early or late in its throw.",
                detail: "A dropdown of DOTween easing curves. \"In\" curves (InQuad, InCubic, InQuart...) keep " +
                    "the ball small off the line and grow it late — the deeper the curve, the later the " +
                    "growth. Linear grows evenly. \"Out\" curves pop it to near-full size immediately.",
            },
            {
                label: "SpawnStrokeEase", code: "SpawnStrokeEase", kind: "enum", default: "InCubic",
                summary: "Whether the ball's outline rim appears early or late in its throw.",
                detail: "The outline grows from nothing (the in-box look) to the full active rim across the " +
                    "throw. Same dropdown of DOTween curves as SpawnScaleEase, on its own setting so the " +
                    "rim can trail or lead the size growth.",
            },
            {
                label: "DespawnY", code: "DespawnY", kind: "float", default: "-4", unit: "world Y",
                summary: "The height below which a fallen ball is deleted and counted as gone.",
                warn: "Set this too high and balls vanish while still on screen. Too low and escaped balls fall " +
                    "forever, quietly holding a slot against Max Balls In Play.",
            },
            {
                label: "Max Up Speed (0 = off)", code: "ScrewMaxUpSpeed", kind: "float", default: "5", unit: "units/sec",
                summary: "A speed limit that only applies to going UP.",
                detail: "The three speed limits are one per direction — up, down, sideways — because one shared " +
                    "limit can't be tuned for all three: slowing a launch enough to keep it in the basin also " +
                    "slows every honest drop.\n\n" +
                    "This is the upward one. When balls get squeezed out of a pile they mostly go up — over " +
                    "the funnel wall and somewhere they shouldn't be.",
                tip: "At 5 a ball can still lift out of a squeeze, but can't climb fast enough to clear the funnel wall. 0 turns it off.",
            },
            {
                label: "Max Down Speed (0 = off)", code: "ScrewMaxDownSpeed", kind: "float", default: "30", unit: "units/sec",
                summary: "The falling speed limit — a loose terminal velocity.",
                detail: "At 30 an ordinary board-height drop never touches it; it only reins in the extreme cases, " +
                    "like a ball spat downward out of a deep overlap.",
                tip: "Bring it down toward 10 and every drop turns slow and heavy — balls arrive at the funnel with much less energy to wedge themselves in with.",
            },
            {
                label: "Max Horizontal Speed (0 = off)", code: "ScrewMaxHorizontalSpeed", kind: "float", default: "10", unit: "units/sec",
                summary: "The sideways speed limit, both directions.",
                detail: "Bounds the sideways part of a physics fling — two deeply overlapping circles being " +
                    "separated can shoot one across the board — without touching how fast balls fall.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "jam",
        label: "Jam",
        blurb: "Loose balls sometimes bridge into an arch over the holder's mouth and go to sleep, so nothing feeds " +
            "the conveyor and the game quietly stops. These values shove the pile until it collapses.",
        options: [
            {
                label: "Breaker Enabled", code: "JamBreakerEnabled", kind: "bool", default: "on",
                summary: "The master switch. Off leaves a jam standing instead of shoving it loose.",
                tip: "Turn it off when you want to actually watch a jam form — the rescue fires in a fifth of a second and hides the pile it fixed.",
            },
            {
                label: "Detect Time (s, 0=off)", code: "JamDetectTime", kind: "float", default: "0.2", unit: "seconds",
                figure: "jam",
                summary: "How long balls must sit still, with the holder empty, before the game shoves them.",
                detail: "It needs BOTH conditions: balls stopped AND the holder starving. A full holder means the " +
                    "conveyor is simply backed up, which is normal, not a jam.",
                tip: "Kept short so a jam clears almost as soon as it forms — but not so short it nudges a ball " +
                    "at the top of its arc, where it's briefly almost stopped. 0 disables the rescue entirely.",
            },
            {
                label: "Nudge Horizontal", code: "JamNudgeHorizontal", kind: "float", default: "1.5", unit: "units/sec",
                summary: "How hard each stuck ball is flicked sideways, randomly left or right.",
                detail: "This is the one that actually breaks the jam: an arch holds because it's symmetrical, and a random sideways flick ruins that.",
                tip: "Too small and the arch survives. Too big and you spray balls across the board.",
            },
            {
                label: "Nudge Down", code: "JamNudgeDown", kind: "float", default: "0.5", unit: "units/sec",
                summary: "A little extra downward shove added to each flick.",
                detail: "Once the arch breaks, this makes sure the pile falls into the holder instead of milling about.",
            },
            {
                label: "Rescue Enabled", code: "FunnelRescueEnabled", kind: "bool", default: "on",
                summary: "The backstop for a pile the nudges can't break: carries one ball into the funnel's mouth.",
                detail: "When loose balls are in play but none of them is at the Entrance — the Entrance zone and the " +
                    "holder are empty and nothing is dropping onto the belt — for the Rescue Delay, the loose ball " +
                    "nearest the Entrance slides into it. From there the entrance feed and the holder handle it like " +
                    "any other ball, and the balls resting on it follow it down.",
            },
            {
                label: "Rescue Delay (s, 0=off)", code: "FunnelRescueDelay", kind: "float", default: "5", unit: "seconds",
                summary: "How long the funnel's mouth must sit empty, with loose balls about, before a ball is carried in.",
                detail: "The clock restarts whenever a ball reaches the Entrance or the holder, or a drop onto the belt is " +
                    "in the air — so a normal throw, which reaches the mouth in about a second, never triggers it.",
                tip: "Long enough that the nudges get their chance first. 0 disables the rescue.",
            },
            {
                label: "Rescue Move Duration", code: "FunnelRescueMoveDuration", kind: "float", default: "0.35", unit: "seconds",
                summary: "How long the carried ball takes to slide into the Entrance.",
            },
            {
                label: "Rescue Ease", code: "FunnelRescueEase", kind: "enum", default: "InOutSine",
                summary: "The speed shape of that slide — a dropdown of DOTween easing curves.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "box",
        label: "Box",
        blurb: "Boxes dropping their nine eggs and popping back out. At level start the boxes are simply there — " +
            "no spawn-in wave — so the pop-in values below only apply to a box arriving mid-game out of a tunnel.",
        options: [
            {
                label: "Screw Spawn Delay (frames)", code: "ScrewSpawnDelayFrames", kind: "int", default: "2", unit: "frames",
                summary: "The gap between each egg leaving, left to right along a row — counted in frames.",
                detail: "Counted in FRAMES rather than seconds on purpose: a seconds-based wait can only wake on " +
                    "a frame anyway, so it rounded each gap to 1 or 2 frames at random — and once the eggs " +
                    "are falling, gravity stretches every launch-gap difference into a different spacing, so " +
                    "each row's trail looked different. Exact frame counts make every gap — and every " +
                    "row's trail — identical. 2 frames at 60fps is about 33ms.",
            },
            {
                label: "Row Spawn Delay (frames)", code: "RowSpawnDelayFrames", kind: "int", default: "5", unit: "frames",
                summary: "The stagger between one row STARTING to empty and the next, bottom to top — counted in frames.",
                detail: "The three rows empty independently, in parallel: each steps through its own eggs on " +
                    "Ball Spawn Delay, and this is only the head start each row gets on the one above it — a " +
                    "slow egg rhythm never holds the next row back. 5 frames at 60fps is about 83ms.",
                tip: "These two together are the whole 'rrrip' of a box emptying. Raise both to see the order clearly.",
            },
            {
                label: "Spawn-In Duration", code: "BoxSpawnDuration", kind: "float", default: "0.25", unit: "seconds",
                summary: "How long a tunnel-fed box takes to pop in, from nothing to full size.",
                detail: "Level-start boxes don't animate in at all — this only plays when a tunnel spits a box into an empty cell.",
            },
            {
                label: "Spawn-In Overshoot", code: "BoxSpawnOvershoot", kind: "float", default: "1.7", figure: "bounce",
                summary: "How much that box swells past full size before settling, as it pops in.",
                detail: "0 means it just eases to full with no bounce. Around 1.7 it goes about 10% past and springs back.",
                tip: "Every board bounce shares this curve, including the empty base that drops into a vacated cell.",
            },
            {
                label: "Destroy Grow Duration", code: "BoxDestroyBounceDuration", kind: "float", default: "0.06", unit: "seconds",
                summary: "How long the empty box swells up for, just before it vanishes.",
            },
            {
                label: "Destroy Grow Scale", code: "BoxDestroyBounceScale", kind: "float", default: "0.15",
                summary: "How much it swells — 0.22 means it puffs up to 122% before going.",
            },
            {
                label: "Destroy Shrink Duration", code: "BoxDestroyShrinkDuration", kind: "float", default: "0.21", unit: "seconds",
                summary: "How long it then takes to shrink away to nothing.",
                detail: "Destroying the box is what frees the cell, so a tunnel can refill it right after this.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "boxOpen",
        label: "Box Open",
        blurb: "Only about the moment a CLOSED box is revealed. The eggs appear up front, the box presses flat " +
            "(wider, shorter), jumps (growing a little), lands, bounces and settles. The lid rides the jump, leaves the box " +
            "at the release point and keeps rising, shrinking a little and fading out on the way up.",
        options: [
            {
                label: "Squash Scale X", code: "BoxOpenSquashScaleX", kind: "float", default: "1.25", figure: "boxOpen",
                summary: "How wide the box gets at the bottom of the press — 1.25 means 125%.",
            },
            {
                label: "Squash Scale Y", code: "BoxOpenSquashScaleY", kind: "float", default: "0.62",
                summary: "How flat the box gets at the bottom of the press — 0.62 means 62% height.",
            },
            {
                label: "Squash Duration", code: "BoxOpenSquashDuration", kind: "float", default: "0.07", unit: "seconds",
                summary: "How long the press-down takes; the lid squeezes along with it.",
            },
            {
                label: "Settle Duration", code: "BoxOpenSettleDuration", kind: "float", default: "0.18", unit: "seconds",
                summary: "How long the box wobbles after landing before it rests.",
            },
            {
                label: "Jump Height", code: "BoxOpenJumpHeight", kind: "float", default: "0.25", unit: "box units",
                summary: "How high the box jumps off the board after the press.",
            },
            {
                label: "Jump Duration", code: "BoxOpenJumpDuration", kind: "float", default: "0.18", unit: "seconds",
                summary: "Time in the air, takeoff to landing.",
            },
            {
                label: "Jump Scale", code: "BoxOpenJumpScale", kind: "float", default: "1.1",
                summary: "How big the box grows at the top of the jump; after landing it dips below normal by about as much, then bounces and settles.",
            },
            {
                label: "Jump Shadow Scale", code: "BoxOpenJumpShadowScale", kind: "float", default: "0.8",
                summary: "How small the grounded shadow gets at the top of the jump.",
            },
            {
                label: "Screw Lag", code: "BoxOpenScrewLag", kind: "float", default: "0.07", unit: "seconds",
                summary: "How much later than the box the balls touch down. They hop off the tray at the top of the box's jump and land this long after the box (plus the row and middle delays).",
            },
            {
                label: "Screw Jump Height", code: "BoxOpenScrewJumpHeight", kind: "float", default: "0.12", unit: "box units",
                summary: "How high the balls hop off the tray, starting at the top of the box's jump.",
            },
            {
                label: "Screw Row Delay", code: "BoxOpenScrewRowDelay", kind: "float", default: "0.015", unit: "seconds",
                summary: "Extra touch-down delay per row: the top row lands first, the middle row this much later, the bottom row twice this.",
            },
            {
                label: "Screw Middle Delay", code: "BoxOpenScrewMiddleDelay", kind: "float", default: "0.015", unit: "seconds",
                summary: "Extra touch-down delay for the middle ball of each row, so it lands just after the two beside it.",
            },
            {
                label: "Screw Squash", code: "BoxOpenScrewSquash", kind: "float", default: "0.2",
                summary: "How hard each ball squashes when it touches down after the box lands. 0 = no squash.",
            },
            {
                label: "Screw Squash Duration", code: "BoxOpenScrewSquashDuration", kind: "float", default: "0.22", unit: "seconds",
                summary: "How long the balls' squash-and-wobble takes to settle.",
            },
            {
                label: "Lid Lift Height", code: "BoxOpenLidLiftHeight", kind: "float", default: "0.42", unit: "world units",
                summary: "How far the lid keeps rising after it leaves the box.",
            },
            {
                label: "Lid Lift Ease", code: "BoxOpenLidLiftEase", kind: "enum", default: "OutQuad",
                summary: "The curve of the lid's rise — OutQuad launches fast and slows into the top.",
                detail: "A dropdown of DOTween easing curves for the lift only. \"Out\" curves start fast and " +
                    "ease to a stop (the pop-off feel); Linear rises evenly; \"In\" curves start slow and " +
                    "accelerate.",
            },
            {
                label: "Lid Scale Ease", code: "BoxOpenLidScaleEase", kind: "enum", default: "InQuad",
                summary: "The curve of the lid's shrink to Lid End Scale, over its rise.",
                detail: "A dropdown of DOTween easing curves. \"Out\" curves dump most of the shrink early; " +
                    "\"In\" curves hold the lid near full size and vanish it late; InOutQuad is the gentle S-curve.",
            },
            {
                label: "Lid Lift Fraction", code: "BoxOpenLidLiftFraction", kind: "float", default: "0.35", unit: "0–1",
                summary: "How much of the lid's exit (release to the end) the rise takes. The lid shrinks and fades over the same rise.",
            },
            {
                label: "Lid Release At", code: "BoxOpenLidReleaseAt", kind: "float", default: "0.1", unit: "0–1",
                summary: "Point in the jump where the lid leaves the box and keeps rising on its own. 0.5 = the top of the jump.",
            },
            {
                label: "Lid End Scale", code: "BoxOpenLidEndScale", kind: "float", default: "0.6",
                summary: "How small the lid gets by the time it has faded out.",
            },
            {
                label: "Lid Fade Start", code: "BoxOpenLidFadeStart", kind: "float", default: "0.3", unit: "0–1",
                summary: "Point in the lid's rise where it starts fading; it is gone at the top.",
            },
            {
                label: "Spot Fade Duration", code: "BoxOpenSpotFadeDuration", kind: "float", default: "0.22", unit: "seconds",
                summary: "How long the egg cups take to fade in once the lid is gone.",
            },
            {
                label: "Surprise Box Screw Fade In", code: "SurpriseBoxScrewFadeInDuration", kind: "float", default: "0.35", unit: "seconds",
                summary: "How long the screws of a Super Mystery Box take to fade in as its lid lifts.",
            },
            {
                label: "Surprise Box Lid Exit", code: "SurpriseBoxLidExitDuration", kind: "float", default: "0.22", unit: "seconds",
                summary: "How long the Super Mystery Box's lid takes to rise and disappear after the tap. Lower = gone faster.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "boxRefuse",
        label: "Box Refuse",
        blurb: "What a box does when the player taps it and it cannot answer: a closed box whose turn has not come, " +
            "or an open box while the belt has no room for its nine screws (the \"Board Full!\" tap). It wobbles " +
            "side to side on its base and settles, with a soft sound and a light \"no\" haptic, so the tap is " +
            "seen and felt to have landed. Before this, a closed box " +
            "did nothing at all. Chosen in a browser preview against a lid rattle.",
        options: [
            {
                label: "Shake", code: "BoxRefuseShakeEnabled", kind: "bool", default: "on",
                summary: "Whether a refused tap wobbles the box.",
                detail: "Off = the old behaviour: a closed box ignores the tap without any sign.",
            },
            {
                label: "Shake Angle", code: "BoxRefuseShakeAngle", kind: "float", default: "6", unit: "degrees",
                summary: "The widest the box turns.",
            },
            {
                label: "Shake Swings", code: "BoxRefuseShakeSwings", kind: "float", default: "2.5",
                summary: "How many side-to-side swings it makes while dying out.",
                detail: "2.5 reads as a quick \"no-no\"; fewer feels like a nudge, more like a rattle.",
            },
            {
                label: "Shake Duration", code: "BoxRefuseShakeDuration", kind: "float", default: "0.3", unit: "seconds",
                summary: "How long the wobble lasts.",
            },
            {
                label: "Sound", code: "BoxRefuseSound", kind: "bool", default: "on",
                summary: "A soft, dull \"tuk\" with the wobble.",
                detail: "Deliberately not an error buzzer: it says \"not now\", not \"wrong\". It is the sound the browser " +
                    "preview made, rendered to Assets/Audio/BoxRefuse by tools/gen_box_refuse_sound.py at full level, as " +
                    "loud as the other effects. Obstacles play it too (see Obstacle Refuse).",
            },
            {
                label: "Haptic", code: "BoxRefuseHaptic", kind: "enum", default: "Nope",
                summary: "The vibration with the wobble.",
                detail: "Nope is iOS's own light \"rejected\" buzz — three weak pulses — distinct from the single tick a " +
                    "landing screw gives. Error and Warning are the stronger system notifications; None turns it off.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "obstacleRefuse",
        label: "Obstacle Refuse",
        blurb: "What an obstacle does when the player taps it, or taps a box it holds. Each answers with its own " +
            "motion instead of the box wobbling under it: a connected pair rocks as one piece, the bolt tugs " +
            "toward its head, the ice punches and flashes, the laser beams stutter, the lock panel kicks and its " +
            "count pops, the metal box's chains rattle, the sliding door tries to wind in, a closed mystery cover " +
            "wobbles like a box, and a locked arrow box's arrow pushes toward the cell it waits on. All of them " +
            "play Box Refuse's sound and haptic (the metal box adds a chain rattle; a laser crackles instead, and its " +
            "key nudges on its box). A gate (the multiplier bar) answers with the ring a passing ball sets off and a hologram " +
            "shimmer, with no haptic. A tool box wobbles like a closed box. Tunnels, pipes and the elevator do not answer: " +
            "tapping them is no move. Chosen in a browser preview of every obstacle.",
        options: [
            {
                label: "Enabled", code: "ObstacleRefuseEnabled", kind: "bool", default: "on",
                summary: "Whether obstacles answer refused taps with their own motion.",
                detail: "Off = the earlier behaviour: the box wobbles alone under a still bolt, ice or laser, a " +
                    "connected box wobbles without its partner, covers and gates ignore taps, and tapping a held " +
                    "box on a full board shows \"Board Full!\".",
            },
            {
                label: "Pair Angle Share", code: "ObstacleRefusePairShare", kind: "float", default: "0.5",
                summary: "How far a connected pair rocks, as a share of Box Refuse's Shake Angle.",
                detail: "The pair is two boxes long, so half the angle moves its ends about as far as one box's.",
            },
            {
                label: "Bolt Tug", code: "ObstacleRefuseBoltTug", kind: "float", default: "0.14", unit: "art units",
                summary: "How far the bolt pulls toward its head (a cell is 1.485). The boxes on it drag 30% of that.",
            },
            {
                label: "Bolt Duration", code: "ObstacleRefuseBoltDuration", kind: "float", default: "0.32", unit: "seconds",
                summary: "How long the bolt's two tugs take.",
            },
            {
                label: "Ice Punch", code: "ObstacleRefuseIcePunch", kind: "float", default: "0.06",
                summary: "How much the ice and the box in it swell and shrink, as a fraction of their size.",
            },
            {
                label: "Ice Flash", code: "ObstacleRefuseIceFlash", kind: "float", default: "0.6",
                summary: "How far past white the ice flashes. 0 = no flash.",
            },
            {
                label: "Ice Duration", code: "ObstacleRefuseIceDuration", kind: "float", default: "0.3", unit: "seconds",
                summary: "How long the ice's punch and flash take.",
            },
            {
                label: "Laser Flicker", code: "ObstacleRefuseLaserFlicker", kind: "float", default: "0.8",
                summary: "How hard the beams stutter: how dark they dip and how far past white they flare.",
            },
            {
                label: "Laser Fence Angle", code: "ObstacleRefuseLaserFenceAngle", kind: "float", default: "2.5", unit: "degrees",
                summary: "The widest the two gates rattle.",
            },
            {
                label: "Laser Duration", code: "ObstacleRefuseLaserDuration", kind: "float", default: "0.32", unit: "seconds",
                summary: "How long the stutter takes.",
            },
            {
                label: "Vault Kick", code: "ObstacleRefuseVaultKick", kind: "float", default: "3", unit: "degrees",
                summary: "The widest the lock panel rocks.",
            },
            {
                label: "Vault Count Pop", code: "ObstacleRefuseVaultPop", kind: "float", default: "1.175",
                summary: "How big the panel's count grows before it settles. 1 = no pop.",
            },
            {
                label: "Vault Duration", code: "ObstacleRefuseVaultDuration", kind: "float", default: "0.32", unit: "seconds",
                summary: "How long the kick and the pop take.",
            },
            {
                label: "Metal Chain Angle", code: "ObstacleRefuseMetalChainAngle", kind: "float", default: "5", unit: "degrees",
                summary: "The widest the metal box's chains swing.",
            },
            {
                label: "Metal Flex", code: "ObstacleRefuseMetalFlex", kind: "float", default: "0.035",
                summary: "How far the plate bows, as a fraction of its height. 0 = no bow.",
                detail: "A snapping chain bows it by Metal Box's Flex Height (0.06); this is a smaller version.",
            },
            {
                label: "Metal Duration", code: "ObstacleRefuseMetalDuration", kind: "float", default: "0.34", unit: "seconds",
                summary: "How long the rattle and the bow take.",
            },
            {
                label: "Door Tug", code: "ObstacleRefuseDoorTug", kind: "float", default: "0.12", unit: "art units",
                summary: "How far the sliding door's tape pulls toward its housing (a cell is 1.485).",
            },
            {
                label: "Door Duration", code: "ObstacleRefuseDoorDuration", kind: "float", default: "0.32", unit: "seconds",
                summary: "How long the door's two tugs take.",
            },
            {
                label: "Laser Key Wiggle", code: "ObstacleRefuseLaserKeyWiggle", kind: "float", default: "10", unit: "degrees",
                summary: "When a laser gate is tapped, how far its key wiggles on its box. 0 = the key stays still.",
                detail: "The key also starts to turn upright and swells a little, then settles: it points the player at the box that opens the gate.",
            },
            {
                label: "Laser Key Duration", code: "ObstacleRefuseLaserKeyDuration", kind: "float", default: "0.5", unit: "seconds",
                summary: "How long the key's nudge takes.",
            },
            {
                label: "Multiplier Sound", code: "ObstacleRefuseMultiplierSound", kind: "bool", default: "on",
                summary: "A short, quiet hologram shimmer with the gate's ring.",
                detail: "Rendered by tools/gen_obstacle_refuse_sounds.py into Assets/Audio/GateTap.",
            },
            {
                label: "Laser Buzz Sound", code: "ObstacleRefuseLaserBuzzSound", kind: "bool", default: "on",
                summary: "A laser answers with an electric crackle instead of the shared refuse sound.",
                detail: "A buzz with sparks, loud on the beams' bright beats and quiet on the dim ones, rendered by tools/gen_obstacle_refuse_sounds.py into Assets/Audio/LaserGateBuzz. Off = the shared \"tuk\".",
            },
            {
                label: "Metal Rattle Sound", code: "ObstacleRefuseMetalRattleSound", kind: "bool", default: "on",
                summary: "The metal box's chains rattle audibly, on top of the shared refuse sound.",
                detail: "Five dull iron clinks on the beats of the chains' swing, rendered by tools/gen_obstacle_refuse_sounds.py into Assets/Audio/MetalBoxRattle.",
            },
            {
                label: "Arrow Dart", code: "ObstacleRefuseArrowDart", kind: "float", default: "0.12", unit: "art units",
                summary: "How far a locked arrow box's arrow pushes toward the cell it waits on.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "mysteryParticles",
        label: "Mystery Particles",
        blurb: "The burst of question marks and dots when a mystery box or a mystery slice shows its colour.",
        options: [
            {
                label: "Colors", code: "MysteryParticleColors", kind: "enum", default: "WhiteGray",
                summary: "WhiteGray: each question mark and dot gets its own random shade between white and gray. Colored: they take the colour of the box or slice. Original: the prefab's own colours (question marks white to pink, dots white).",
            },
            {
                label: "Box Amount", code: "BoxMysteryAmount", kind: "int", default: "8",
                summary: "How many question marks, and how many dots, a mystery box bursts out (each gets this many).",
            },
            {
                label: "Box Lifetime Min", code: "BoxMysteryLifetimeMin", kind: "float", default: "0.375", unit: "seconds",
                summary: "Shortest a mystery box question mark or dot lives.",
            },
            {
                label: "Box Lifetime Max", code: "BoxMysteryLifetimeMax", kind: "float", default: "0.975", unit: "seconds",
                summary: "Longest a mystery box question mark or dot lives.",
            },
            {
                label: "Box Radius", code: "BoxMysteryRadius", kind: "float", default: "0.7125", unit: "box units",
                summary: "Radius of the circle the mystery box particles spawn in. Lower = closer to the box middle.",
            },
            {
                label: "Slice Amount", code: "SliceMysteryAmount", kind: "int", default: "8",
                summary: "How many question marks, and how many dots, a mystery slice bursts out (each gets this many).",
            },
            {
                label: "Slice Lifetime Min", code: "SliceMysteryLifetimeMin", kind: "float", default: "0.375", unit: "seconds",
                summary: "Shortest a mystery slice question mark or dot lives.",
            },
            {
                label: "Slice Lifetime Max", code: "SliceMysteryLifetimeMax", kind: "float", default: "0.975", unit: "seconds",
                summary: "Longest a mystery slice question mark or dot lives.",
            },
            {
                label: "Slice Radius", code: "SliceMysteryRadius", kind: "float", default: "3.55", unit: "world units",
                summary: "Radius of the ring arc the mystery slice particles spawn on, measured from the board centre.",
            },
            {
                label: "Slice ? Size Min", code: "SliceMysteryMarkSizeMin", kind: "float", default: "0.2", unit: "world units",
                summary: "Smallest start size of a question mark in a mystery slice's reveal burst (the dots keep their own size).",
            },
            {
                label: "Slice ? Size Max", code: "SliceMysteryMarkSizeMax", kind: "float", default: "0.35", unit: "world units",
                summary: "Largest start size of a question mark in a mystery slice's reveal burst (the dots keep their own size).",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "connector",
        label: "Link",
        blurb: "The middle piece between two connected boxes, which pops off and fades when they drop.",
        options: [
            {
                label: "Dismiss Rise Duration", code: "LinkDismissRiseDuration", kind: "float", default: "0.195", unit: "seconds",
                summary: "How long the piece takes to fly up to the top of its arc.",
            },
            {
                label: "Dismiss Fall Duration", code: "LinkDismissFallDuration", kind: "float", default: "0.455", unit: "seconds",
                summary: "How long the piece falls after the top of its arc. The fade is set separately (Dismiss Fade Delay / Duration).",
            },
            {
                label: "Dismiss Fade Delay", code: "LinkDismissFadeDelay", kind: "float", default: "0", unit: "seconds",
                summary: "How long after the top of its arc the piece starts fading. 0 = fades from the top of the arc.",
            },
            {
                label: "Dismiss Fade Duration", code: "LinkDismissFadeDuration", kind: "float", default: "0.455", unit: "seconds",
                summary: "How long the piece takes to fade out. Independent of the fall; if the fade runs past the end of the fall the piece holds its last spot while it finishes fading.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "magnet",
        label: "Magnet",
        blurb: "The magnet booster's targeting dresses every pickable box in marker covers that breathe a highlight tint.",
        options: [
            {
                label: "Cover Pulse Frequency", code: "MagnetCoverPulseFrequency", kind: "float", default: "0.5", unit: "breaths/second",
                summary: "How fast the covers breathe white → tint → white while the targeting waits (0.5 = one breath every 2 seconds).",
            },
            {
                label: "Cover Pulse Tint (RRGGBB)", code: "MagnetCoverPulseTintHex", kind: "string", default: "BFBFBF",
                summary: "The breath's peak colour as RRGGBB hex, no '#'.",
                detail: "A tint MULTIPLIES the sprite, so it can only warm or darken the art, never brighten " +
                    "past it — BFBFBF is the stock neutral dim (a shade-down breath with no colour cast). " +
                    "An unparseable value falls back to that dim rather than flashing.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "shuffle",
        label: "Twister",
        blurb: "Which slices the shuffle booster brings to the front row.",
        options: [
            {
                label: "Wheel Match", code: "TwisterWheelMatch", kind: "bool", default: "on",
                summary: "Selected: after the deal, if no front slice takes a screw on the belt, one front slice is swapped for the belt's most common colour. Not selected: the deal alone.",
                detail: "The deal runs as before (front slices aimed at colours the row didn't show). Then, if none of the front " +
                    "slices is in a colour on the belt, the belt's colours are tried by how many screws they have: the first " +
                    "one found comes from a random visible preview, or else the hidden slice of that colour nearest the front, " +
                    "and swaps with a random front slice, which takes its place. Only the belt counts, not the funnel; with " +
                    "nothing on the belt nothing changes. Frozen slices never move and a frozen front slice doesn't count.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "cannon",
        label: "Screw Gun",
        blurb: "The cannon booster: it drives in from the left, runs a little past its spot and eases back onto it (the barrel trailing the way it travels and coming upright as it stops), backs off a little to the left once the last shot is away and then drives off to the right (trailing again), balls hop into it in a projectile arc, its screw head slides open to let the screws in and shuts once charged, and five lamps show the next shots in their colours.",
        fixed: [
            {
                label: "Slide", value: "45° diagonals, 0.4 units",
                note: "Each quarter slides straight back along its own diagonal, away from the intake's centre and under the body shell. The distance is on the CannonBooster prefab (Screw Door Open Distance), not here.",
            },
        ],
        options: [
            {
                label: "Enter Duration", code: "ScrewGunEnterDuration", kind: "float", default: "0.45", unit: "seconds",
                summary: "How long the cannon takes to drive in from off the left edge of the screen to its spot.",
                detail: "It starts just past the left edge, whatever the screen's width, and the pull-in only begins " +
                    "once it has arrived — so this adds straight onto the booster's run time.",
                tip: "0 skips the drive-in: the cannon appears in place, as it used to.",
            },
            {
                label: "Enter Ease", code: "ScrewGunEnterEase", kind: "enum", default: "OutQuint",
                summary: "The speed shape of the drive-in — the car's throttle and brake.",
                detail: "The Out family is the fast car braking: OutQuint arrives hot and stamps on the brake at " +
                    "the very end, OutCubic brakes earlier and softer, OutExpo harder still. A Back ease " +
                    "overshoots on top of Enter Overshoot.",
            },
            {
                label: "Enter Lean Angle", code: "ScrewGunEnterLeanAngle", kind: "float", default: "8", unit: "degrees",
                summary: "How far the barrel trails back (muzzle left) at the drive-in's top speed, like a passenger pressed into the seat.",
                detail: "The lean follows the SPEED: the barrel hangs like a pendulum, so as the cannon brakes it " +
                    "swings past upright and settles, and it trails right while the cannon rolls back (Enter " +
                    "Return Lean Angle).",
                tip: "0 has it ride in upright.",
            },
            {
                label: "Enter Overshoot", code: "ScrewGunEnterOvershoot", kind: "float", default: "1.5", unit: "world units",
                summary: "How far the drive-in runs on past the cannon's spot before it eases back onto it.",
                detail: "The drive-in brakes to a standstill this far to the right of the spot, then the cannon rolls " +
                    "back onto it over Enter Return Duration — a car that pulls up a touch too far.",
                tip: "0 has it stop dead on its spot.",
            },
            {
                label: "Enter Return Duration", code: "ScrewGunEnterReturnDuration", kind: "float", default: "0.2", unit: "seconds",
                summary: "How long the roll back from the overshoot onto the spot takes.",
                detail: "Adds onto the drive-in: the pull-in starts once the cannon is back on its spot.",
                tip: "0 skips the overshoot.",
            },
            {
                label: "Enter Settle Bounce", code: "ScrewGunEnterSettleBounce", kind: "float", default: "0.15", unit: "x overshoot",
                summary: "After the overshoot, how far the cannon swings past its spot the other way, as a share of the overshoot.",
                detail: "A swing a third of that comes back over, then it settles - a cartoon skid-and-wobble. The first swing back takes Enter Return Duration, the next two 0.65x and 0.6x of it.",
                tip: "0 has it come straight back onto the spot.",
            },
            {
                label: "Enter Return Ease", code: "ScrewGunEnterReturnEase", kind: "enum", default: "InOutSine",
                summary: "The speed shape of the roll back onto the spot.",
                detail: "An InOut ease starts from the standstill the drive-in braked to and settles softly; an Out " +
                    "ease would jerk off the turn-round.",
            },
            {
                label: "Enter Return Lean Angle", code: "ScrewGunEnterReturnLeanAngle", kind: "float", default: "4", unit: "degrees",
                summary: "How far the barrel trails (muzzle right) at the roll back's top speed.",
                detail: "It swings back through upright at the turn-round and again as the cannon settles, like a pendulum.",
                tip: "0 has it hang upright on the way back.",
            },
            {
                label: "Wait Before Pull-In", code: "ScrewGunWaitBeforePullIn", kind: "float", default: "0.25", unit: "seconds",
                summary: "How long the cannon holds still on its spot after driving in, before it starts sucking the balls in.",
                detail: "The barrel settles meanwhile; the balls it has claimed ride on around the belt, and the belt feed stays off until the burst is done.",
                tip: "0 has it start opening its doors as soon as it arrives; the first ball still leaves 0.12 s after.",
            },
            {
                label: "Wait Before Firing", code: "ScrewGunWaitBeforeFiring", kind: "float", default: "0.3", unit: "seconds",
                summary: "How long the loaded cannon holds once every ball is inside, before its first shot.",
                detail: "The screw doors shut and the barrel turns onto the first target meanwhile; the belt feed stays off until the burst is done.",
                tip: "0 has it fire as soon as it is charged.",
            },
            {
                label: "Wait After Firing", code: "ScrewGunWaitAfterFiring", kind: "float", default: "0.25", unit: "seconds",
                summary: "How long the cannon stays after its last shot before it drives off.",
                detail: "Play has already resumed by then (the belt is handed back first), so this only delays the next booster.",
                tip: "0 has it leave straight away.",
            },
            {
                label: "Exit Duration", code: "ScrewGunExitDuration", kind: "float", default: "0.45", unit: "seconds",
                summary: "How long the cannon takes to drive off past the right edge of the screen once the last shot is away.",
                detail: "The belt is handed back to play before it sets off, so the drive-out never holds the game " +
                    "up — only the next booster waits for it.",
                tip: "0 skips the drive-out: the cannon vanishes in place, as it used to.",
            },
            {
                label: "Exit Ease", code: "ScrewGunExitEase", kind: "enum", default: "InCubic",
                summary: "The speed shape of the drive-out — the car pulling away.",
                detail: "The In family starts slow and speeds off. InCubic gets going soon enough for the barrel's " +
                    "lean to build while the cannon is still on screen; InQuint sits a beat and then floors it " +
                    "(the entrance's OutQuint played backwards), so most of the lean happens off-screen.",
            },
            {
                label: "Exit Lean Angle", code: "ScrewGunExitLeanAngle", kind: "float", default: "8", unit: "degrees",
                summary: "How far the barrel trails back (muzzle left) at the drive-out's top speed.",
                detail: "The barrel first turns off its last aim to the middle (a controlled turn, not a swing), while " +
                    "the lean builds with the speed and swings like a pendulum.",
                tip: "0 has it leave upright.",
            },
            {
                label: "Impact Depth", code: "ScrewGunImpactDepth", kind: "float", default: "0.06", unit: "world units",
                summary: "How far one cannon shot springs the slice it lands in down before it bounces back.",
                detail: "Each landing kicks the slices around the landing spot down like a spring: the slice it lands in by this much, the ones near it less (Impact Radius, Neighbour Strength). A kick adds to whatever the spring is doing, so every shot of a burst shows as its own jolt.",
                tip: "0 turns the impact off.",
            },
            {
                label: "Impact Max Depth", code: "ScrewGunImpactMaxDepth", kind: "float", default: "0.16", unit: "world units",
                summary: "The deepest a slice can be sprung, however many shots pile into it.",
                tip: "0 = no cap.",
            },
            {
                label: "Impact Frequency", code: "ScrewGunImpactFrequency", kind: "float", default: "5", unit: "Hz",
                summary: "How snappy the spring is: higher dips and returns faster.",
            },
            {
                label: "Impact Damping", code: "ScrewGunImpactDamping", kind: "float", default: "0.5", unit: "0–1",
                summary: "How quickly the spring calms down.",
                detail: "Low values wobble past rest a few times; near 1 it eases straight back with no bounce.",
            },
            {
                label: "Impact Radius", code: "ScrewGunImpactRadius", kind: "float", default: "2.5", unit: "world units",
                summary: "How far from the landing spot the impact reaches.",
                detail: "Measured to each slice's art centre. A shot into a hidden slice lands on the ring centre, so the innermost slices dip most and the next ring out less.",
                tip: "0 = only the slice it lands in.",
            },
            {
                label: "Impact Neighbour Strength", code: "ScrewGunImpactNeighbourStrength", kind: "float", default: "0.6", unit: "0–1",
                summary: "How hard the slices around the hit one dip, as a share of its kick (before the distance falloff).",
            },
            {
                label: "Exit Sound Delay", code: "ScrewGunExitSoundDelay", kind: "float", default: "0.25", unit: "seconds",
                summary: "How far into the speed-off (after the back-off) the drive-out swoosh starts.",
                detail: "The drive-out builds speed toward its end, and the swoosh (the drive-in one played backwards) " +
                    "peaks ~0.1s after it starts — so this lands its loudest moment on the fastest part.",
                tip: "0 starts it as the cannon sets off.",
            },
            {
                label: "Exit Windup", code: "ScrewGunExitWindup", kind: "float", default: "0.7", unit: "world units",
                summary: "How far the cannon backs off to the left before it drives off to the right.",
                detail: "It eases back to a standstill this far left of its spot, then the drive-out pulls away from " +
                    "there. The barrel's turn off its last aim runs across both.",
                tip: "0 has it pull straight away.",
            },
            {
                label: "Exit Windup Duration", code: "ScrewGunExitWindupDuration", kind: "float", default: "0.33", unit: "seconds",
                summary: "How long the back-off to the left takes.",
                tip: "0 skips the back-off.",
            },
            {
                label: "Exit Windup Ease", code: "ScrewGunExitWindupEase", kind: "enum", default: "InOutSine",
                summary: "The speed shape of the back-off.",
                detail: "An InOut ease ends at a standstill, where the drive-out's In ease picks up — a smooth turn-round.",
            },
            {
                label: "Exit Windup Lean Angle", code: "ScrewGunExitWindupLeanAngle", kind: "float", default: "4", unit: "degrees",
                summary: "How far the barrel trails (muzzle right) at the back-off's top speed.",
                tip: "0 has it hang upright while backing off.",
            },
            {
                label: "Fetch Arc Height", code: "ScrewGunFetchArcHeight", kind: "float", default: "1", unit: "world units",
                summary: "How far above the cannon's intake each ball's hop peaks before it drops in.",
                detail: "The pull-in is a true projectile: the ball rises past the intake (which sits well above the " +
                    "belt), peaks this far above it, and falls in from above. The flight's length is the prefab's " +
                    "Fetch Flight Duration (0.3s), not a setting here.",
                tip: "0 has it slow to a stop right at the intake instead of dropping in.",
            },
            {
                label: "Fetch Peak Scale", code: "ScrewGunFetchPeakScale", kind: "float", default: "1.1",
                summary: "How big the ball swells at the top of its hop, as a multiple of its belt size.",
                detail: "The size rides the same arc as the height: growing as it rises, biggest at the peak, then shrinking to the Fetch Land Scale as it drops in.",
                tip: "1 keeps it belt-sized on the way up.",
            },
            {
                label: "Fetch Land Scale", code: "ScrewGunFetchLandScale", kind: "float", default: "0.9",
                summary: "How big the ball is as it drops into the intake, as a multiple of its belt size.",
                detail: "Under 1 reads as falling down into the hole. It comes back out at its full belt size when fired.",
                tip: "1 has it land at belt size.",
            },
            {
                label: "Door Open Duration", code: "ScrewGunDoorOpenDuration", kind: "float", default: "0.1", unit: "seconds",
                summary: "How long the screw head takes to slide open as the first screw is pulled off the belt.",
                detail: "The open starts the moment the first screw sets off, and a screw's flight into the cannon " +
                    "takes 0.3s, so anything shorter than that has the hole clear before the first one arrives.",
                tip: "0 snaps the doors open with no slide.",
            },
            {
                label: "Door Open Ease", code: "ScrewGunDoorOpenEase", kind: "enum", default: "OutCubic",
                summary: "The speed shape of the open slide.",
                detail: "A dropdown of DOTween curves. The Out family leaves fast and settles into the open pose; " +
                    "the In family starts slow and snaps open at the end; Linear slides at one rate.",
                warn: "A Back or Elastic pick overshoots PAST fully open and springs back — that is the curve, not a bug.",
            },
            {
                label: "Door Close Duration", code: "ScrewGunDoorCloseDuration", kind: "float", default: "0.18", unit: "seconds",
                summary: "How long the screw head takes to slide shut once every screw is inside.",
                detail: "The close is not waited on: the barrel starts swinging onto its first target while the " +
                    "doors are still closing, so a long close never delays the burst.",
                tip: "0 snaps the doors shut with no slide.",
            },
            {
                label: "Door Close Ease", code: "ScrewGunDoorCloseEase", kind: "enum", default: "OutCubic",
                summary: "The speed shape of the close slide.",
                detail: "Same dropdown as the open. An In curve accelerates into the closed pose, which reads as a " +
                    "shut; an Out curve decelerates into it, which reads as a gentle settle.",
                warn: "A Back or Elastic pick overshoots PAST closed, so the quarters briefly cross the centre before settling.",
            },
            {
                label: "Lamp Change Duration", code: "ScrewGunLampChangeDuration", kind: "float", default: "0.1", unit: "seconds",
                summary: "How long one lamp takes to change colour (or turn on/off) when the ring steps — a ball eaten or fired.",
                detail: "Every step changes each lamp whose colour changes — on, off, or to another colour: the " +
                    "colour fades from old to new (from or to grey when the lamp turns on or off), the glow " +
                    "crossfades with it, and the lamp and glow do a small pop. A lamp that gets another ball " +
                    "of the SAME colour does nothing. A new step " +
                    "arriving mid-change starts from wherever the lamp is, so fast charging never jumps.",
                tip: "0 snaps with no animation. Keep it under the time between eats (about 0.05s) for a crisp ratchet, or longer for a smear.",
            },
            {
                label: "Lamp Colour Ease", code: "ScrewGunLampColorEase", kind: "enum", default: "OutQuad",
                summary: "The speed shape of the colour fade (and the glow crossfade).",
                detail: "A lit lamp is its grey art put through the ball colour's gradient map (the artist's maps from the PSB, like the map clipped to the lamp in Photoshop); a change crossfades the old map into the new one. Overshooting eases are clamped for colour.",
            },
            {
                label: "Lamp Pop Scale", code: "ScrewGunLampPopScale", kind: "float", default: "1.15", unit: "× size",
                summary: "How much the lamp and its glow swell at the peak of the change.",
                tip: "1 turns the pop off.",
            },
            {
                label: "Lamp Pop Ease", code: "ScrewGunLampPopEase", kind: "enum", default: "OutQuad",
                summary: "The shape of the pop: the swell follows sin(π · ease(t)), out to the peak and back.",
                detail: "Out eases reach the peak early and settle back slowly; In eases build up and snap back; Linear is a plain symmetric bump.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "tunnel",
        label: "Tunnel",
        blurb: "A tunnel slides a fresh box out of its mouth into the empty cell it points at.",
        options: [
            {
                label: "Emerge Sideways Arc", code: "TunnelEmergeSidewaysArc", kind: "float", default: "1",
                summary: "How much of a quarter circle a LEFT/RIGHT tunnel's box travels on its way out (0 = straight, 1 = full arc).",
                detail: "A sideways mouth points along the row, so the straight line a vertical tunnel " +
                    "uses has the box climbing away from a pipe that is aiming flat. On the arc it " +
                    "leaves along the mouth and bends upward into its cell — horizontal at the " +
                    "start, vertical at the end. Vertical tunnels ignore it: their mouth already " +
                    "points where the box is going.",
            },
            {
                label: "Emerge Duration", code: "TunnelEmergeDuration", kind: "float", default: "0.26", unit: "seconds",
                summary: "How long the slide out of the mouth takes — all the growing happens across it.",
            },
            {
                label: "Land Offset", code: "TunnelEmergeLandOffset", kind: "float", default: "0.2", unit: "world units",
                summary: "How far above its cell the slide aims; the landing settles this far down onto it.",
            },
            {
                label: "Land Duration", code: "TunnelEmergeLandDuration", kind: "float", default: "0.16", unit: "seconds",
                summary: "How long the touch-down settle takes — a small distance, so it reads slow but ends quickly.",
            },
            {
                label: "Land Start Fraction", code: "TunnelEmergeLandStartFraction", kind: "float", default: "0.8",
                summary: "Where in the slide the settle begins (0-1), so the two blend into one motion.",
                detail: "At 0.8 the drop fades in over the slide's last fifth and finishes just after it — " +
                    "no dead stop in the air. 1 = strictly one after the other; lower values start the " +
                    "drop earlier.",
            },
            {
                label: "Emerge Scale Ease (Vertical)", code: "TunnelEmergeScaleEaseVertical", kind: "enum", default: "InCubic",
                summary: "How a TOP/BOTTOM tunnel's box grows from nothing to full size across its slide.",
                detail: "A dropdown of DOTween easing curves. InCubic (the default here) keeps the box " +
                    "small over the first part of the slide and grows it late, so it reads as being " +
                    "pushed out of the pipe. Along the ladder InQuad < InCubic < InQuart < InQuint " +
                    "< InExpo the hold gets longer and the grow more sudden — the hard end holds so " +
                    "long the box looks absent rather than emerging. \"Out\" curves invert it — fast off the " +
                    "mouth, settling toward full size at the end — and Linear grows evenly. " +
                    "Left/right tunnels have their own copy of this — the two flights are " +
                    "different shapes.",
            },
            {
                label: "Emerge Scale Ease (Sideways)", code: "TunnelEmergeScaleEaseSideways", kind: "enum", default: "OutSine",
                summary: "The same curve for a LEFT/RIGHT tunnel's box, which leaves flat and bends up the arc.",
                detail: "Same dropdown as the vertical one, on its own value: a sideways box travels " +
                    "along the row and only turns up into its cell late, so the late grow the " +
                    "vertical mouths use would pop it at the corner — it keeps the gentle OutSine " +
                    "instead. The strength knob below still applies to both.",
            },
            {
                label: "Emerge Ease Strength (Vertical)", code: "TunnelEmergeScaleEaseStrengthVertical", kind: "float", default: "0.1",
                summary: "How much of a TOP/BOTTOM tunnel's picked ease applies — blended toward a plain linear grow.",
                detail: "1 = the curve exactly as picked; 0 = pure linear growth. The stock curves jump " +
                    "in big steps, and this tunes between them. It bites hardest AT THE START: under " +
                    "an \"In\" curve the eased part is still near zero over the first fifth of the " +
                    "slide, so the linear share is most of the box's size there. The default 0.1 " +
                    "(90% linear) grows the box at a near-steady rate out of the mouth, leaving " +
                    "only a faint lean from the picked curve — at this setting the ease dropdown " +
                    "above barely changes anything.",
            },
            {
                label: "Emerge Ease Strength (Sideways)", code: "TunnelEmergeScaleEaseStrengthSideways", kind: "float", default: "0.85",
                summary: "The same knob for a LEFT/RIGHT tunnel's box.",
                detail: "Kept at the 0.85 the emerge shipped with — under the gentle OutSine a sideways " +
                    "box already has size as it leaves the mouth, so it does not need the extra " +
                    "linear floor the vertical grow uses.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "sliding-door",
        label: "Tape Measure",
        blurb: "A sliding door is a tape measure laid across a straight run of boxes, hiding them. Drop a " +
            "box beside it and it pulls one cell back into its housing, handing that box back closed. " +
            "At two cells there is nothing left to pull in, so the last hit takes the whole door away " +
            "and returns both — a door of length N clears in N-1 drops, not N.",
        options: [
            {
                label: "Retract Duration", code: "TapeMeasureRetractDuration", kind: "float", default: "0.32", unit: "seconds",
                summary: "How long one cell of retraction takes.",
                detail: "Every piece slides down the run to the slot the piece beyond it held: the rail " +
                    "nearest the housing slides under it and is gone, the rest shuffle up one, and the " +
                    "cap lands on the new far end.",
                tip: "0 turns the slide off entirely — the door just appears one cell shorter.",
                warn: "The GAME does not wait for this. The box comes back and the door stops sealing it " +
                    "the instant the hit lands, so however long you make this, tapping fast is never " +
                    "held up — a hit landing mid-slide simply cuts the previous one short.",
            },
            {
                label: "Retract Ease", code: "TapeMeasureRetractEase", kind: "enum", default: "OutQuad",
                summary: "How that slide accelerates.",
                detail: "A dropdown of DOTween easing curves. OutQuad pulls away quickly and settles, the " +
                    "way a door on a runner does; an In- curve reads more like a spring-loaded tape " +
                    "snapping home. Linear slides evenly.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "gate",
        label: "Multiplier",
        blurb: "A gate multiplies a ball that passes through it. The ball dives BEHIND the bar, and the " +
            "moment it passes the bar's midline it pops out in front, snapped small, growing back to " +
            "full size — the pass-through squeeze (the old white flash is gone).",
        options: [
            {
                label: "Squeeze Scale", code: "MultiplierSqueezeScale", kind: "float", default: "0.6",
                summary: "Fraction of full size the ball snaps to the instant it passes the bar — the passed ball and its fresh copies alike.",
                warn: "Clamped to 0.05–1. 1 = no squeeze at all.",
            },
            {
                label: "Squeeze Up Duration", code: "MultiplierSqueezeScaleUpDuration", kind: "float", default: "0.2", unit: "seconds",
                summary: "How long the grow back to full size takes.",
            },
            {
                label: "Squeeze Up Ease", code: "MultiplierSqueezeScaleUpEase", kind: "enum", default: "OutBack",
                summary: "How the grow-back accelerates — OutBack bulges slightly past full size before settling.",
                detail: "A dropdown of DOTween easing curves. \"Out\" curves grow fast then settle; OutBack " +
                    "adds a small overshoot bounce that sells the pop. Linear grows evenly.",
            },
            {
                label: "Flash Duration", code: "MultiplierFlashDuration", kind: "float", default: "0.3", unit: "seconds",
                summary: "How long the split's colour flash lasts, strike and afterglow together.",
                detail: "On top of the squeeze, every ball coming out of a split flashes: it brightens and " +
                    "leans toward a lighter tint of itself — peak in the first ~15% of this window — then " +
                    "glides back over the rest, eased at both ends. Nothing changes size.",
                warn: "The flash's COLOUR is no longer tunable here. Each of the 13 ball colours has its own " +
                    "target tint and mix amounts, in code (GateFlashProfiles.cs), tuned in " +
                    "gate-flash-preview.html at the repo root — it shows all 13 balls at rest, at the peak " +
                    "and animating, and exports the table. One shared setting used to drive them all, and it " +
                    "made Pink and Red flash DARKER than they sit at rest while Purple did not move at all.",
            },
            {
                label: "Bend Height", code: "MultiplierBendHeight", kind: "float", default: "0.085",
                summary: "How far the gate's middle bar sags when a ball passes, as a fraction of the bar's height.",
                detail: "A taut net taking a hit: the bar snaps down in the middle, springs back up past rest " +
                    "once, and settles — the same warp as the metal box's flex, pointed down and fast. A " +
                    "ball arriving mid-bend restarts it from wherever the bar is.",
            },
            {
                label: "Bend Duration", code: "MultiplierBendDuration", kind: "float", default: "0.18", unit: "seconds",
                summary: "How long the bar's bend (and the posts' pull and tilt) lasts, from the hit until it settles.",
            },
            {
                label: "Post Pull", code: "MultiplierPostPull", kind: "float", default: "0.25",
                summary: "How far the two end posts slide inward while the bar is bent, as a fraction of how far the bar sags.",
                detail: "The posts follow the bend: they twitch in on the dip and again on the rebound, then " +
                    "return to rest with the bar — the anchors of a stretched net being tugged. 0 = posts stay still.",
            },
            {
                label: "Post Tilt", code: "MultiplierPostTilt", kind: "float", default: "2", unit: "degrees",
                summary: "How far the two end posts tilt their tops toward the middle while the bar is bent.",
                detail: "Measured at the full Bend Height; the bend's deepest dip reaches about 70% of it, so the " +
                    "posts lean about 1.4° at the default. They turn around the point where the bar joins them, " +
                    "and follow the bend like Post Pull — in on the dip, again on the rebound. 0 = no tilt.",
            },
            {
                label: "Shine Duration", code: "MultiplierShineDuration", kind: "float", default: "0.3", unit: "seconds",
                summary: "How long the shine wave takes to grow out past both ends of the bar when a ball passes.",
                detail: "The shine is the bar's brighter version seen only through a ring (a circular Sprite Mask) that " +
                    "starts as a dot where the ball went through (within a cell of the centre), bursts outward and " +
                    "slows toward the ends, fading over its last 40 %. Its outline thickens as it grows, like a " +
                    "spreading wave, and it rides the bar's bend. Every ball that passes spawns its own wave, so a " +
                    "stream of balls sends overlapping waves. A tap on the bar sends one from the centre.",
            },
            {
                label: "Shine Start Scale", code: "MultiplierShineStartScale", kind: "float", default: "0.02",
                summary: "How big the shine ring is when it appears at the bar's centre.",
                detail: "1 = a ring about 1.28 units from the centre (roughly the bar's half-width).",
            },
            {
                label: "Shine End Scale", code: "MultiplierShineEndScale", kind: "float", default: "1.3",
                summary: "How big the shine ring has grown when the wave ends — past both ends of the bar, so it leaves rather than stops.",
            },
            {
                label: "Glow Strength", code: "MultiplierGlowStrength", kind: "float", default: "1",
                summary: "How bright the shine is: how opaque the bar's shiny version gets inside the sweeping ring.",
                detail: "The shine is a brighter copy of the middle bar laid over it, visible only inside the ring; " +
                    "this is how opaque that copy gets there.",
                warn: "Clamped to 0–1. 0 = no glow.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "lockPanel",
        label: "Vault",
        blurb: "A metal plate covering one or more boxes. It counts down as you play, then spins up, bursts apart and frees what " +
            "was underneath.",
        options: [
            {
                label: "Tick Angle", code: "VaultTickAngle", kind: "float", default: "60", unit: "degrees",
                summary: "How far the cover disc turns each time the counter drops by one — left or right, picked at random.",
            },
            {
                label: "Tick Duration", code: "VaultTickDuration", kind: "float", default: "0.2", unit: "seconds",
                summary: "How long each of those turns takes — fast start, easing to a stop.",
            },
            {
                label: "Hit Kick Angle", code: "VaultHitKickAngle", kind: "float", default: "1", unit: "degrees",
                summary: "The small rotation jolt the whole panel gets on every tick.",
            },
            {
                label: "Spin Duration", code: "VaultSpinDuration", kind: "float", default: "1.85", unit: "seconds",
                summary: "After the counter hits zero: how long the cover spins up before the plate bursts.",
            },
            {
                label: "Spin Start Speed", code: "VaultSpinStartSpeed", kind: "float", default: "500", unit: "degrees/second",
                summary: "How fast the cover is already spinning the moment the counter hits zero.",
            },
            {
                label: "Spin End Speed", code: "VaultSpinEndSpeed", kind: "float", default: "1250", unit: "degrees/second",
                summary: "How fast it is spinning right before the plate bursts — it accelerates harder and harder toward this.",
                warn: "Keep it under ~1350. The ring repeats every 45°, so above 22.5° per frame at 60 fps it looks like it spins backwards.",
            },
            {
                label: "Shake Angle", code: "VaultShakeAngle", kind: "float", default: "5", unit: "degrees",
                summary: "How far the whole panel wobbles left and right at the end of the spin-up (it grows from nothing).",
            },
            {
                label: "Shake Start Frequency", code: "VaultShakeStartFrequency", kind: "float", default: "2.4", unit: "Hz",
                summary: "How many left-right wobbles per second the panel makes when the spin-up starts.",
            },
            {
                label: "Shake End Frequency", code: "VaultShakeEndFrequency", kind: "float", default: "11", unit: "Hz",
                summary: "How many wobbles per second it reaches right before the burst — it speeds up harder and harder toward this.",
            },
            {
                label: "Tick Counter (-1)", code: "VaultTickCounter", kind: "action",
                summary: "Knocks one off every lock panel's counter, exactly as if a slice had left the ring.",
                detail: "Goes through the real notification funnel, so the counter text updates, the cover disc " +
                    "turns one notch, and a counter reaching zero spins up and bursts the panel for real.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "screwAnims",
        label: "Screw Anims",
        blurb: "The cosmetic twist a screw makes at the two moments it is being driven in — as it is " +
            "thrown out of its box, and as it seats itself into a slice. Nothing here moves the ball: it " +
            "is the sprite turning, and a whole number of turns ends on the angle it started.",
        options: [
            {
                label: "Drop Delay", code: "ScrewDropDelay", kind: "float", default: "0", unit: "seconds",
                summary: "How long after the ball leaves its box the drop twist starts.",
                detail: "0 fires the instant the ball pops out, which is when the drop begins on screen. It was " +
                    "originally timed against the physics hand-off at the END of the throw arc instead — that " +
                    "moment is invisible, the ball has been arcing the whole time by then, and the turn read as " +
                    "starting well after the fall had. Clamped into the arc, so a delay past 0.2s just fires at its end.",
            },
            {
                label: "Drop Duration", code: "ScrewDropDuration", kind: "float", default: "0.75", unit: "seconds",
                summary: "How long that throw twist takes.",
                detail: "The twist outlives the throw arc rather than being cut short by it, so a value longer than " +
                    "the throw simply keeps the screw turning while the ball is already falling.",
                tip: "Read it together with Drop Turns — the two together are the speed, and past about a " +
                    "turn and a half per second a screw head stops reading as turning and starts to strobe.",
            },
            {
                label: "Drop Ease", code: "ScrewDropEase", kind: "enum", default: "OutExpo",
                summary: "The speed shape of the twist as the ball is thrown out of its box.",
                detail: "OutQuad, the default, is not an approximation — it IS the curve this used to hardcode, " +
                    "at exactly zero error. It bites hard and runs out of steam: twice the average rate at the " +
                    "start, almost nothing at the end, half the revolution done in the first 29% of it. Linear " +
                    "turns at one rate; an In curve winds up slowly and finishes fast.",
            },
            {
                label: "Drop Turns", code: "ScrewDropTurns", kind: "float", default: "0.75", unit: "revolutions",
                summary: "How far the screw spins as it is thrown out of its box.",
                detail: "1 is one full revolution, so the sprite finishes exactly where it started — any whole " +
                    "number does, which is what keeps the twist invisible to everything downstream. Fractions " +
                    "are allowed and leave the screw sitting at a new angle, which the tumble then takes over.",
                tip: "Raise it for a faster-looking bite; 0 turns the drop twist off entirely.",
            },
            {
                label: "Slice Lead", code: "ScrewSliceLead", kind: "float", default: "0.1", unit: "seconds",
                summary: "How early, before the landing settle ENDS, the slice twist starts — up to the moment the ball leaves the belt.",
                detail: "Measured BACKWARDS from the end of the landing scale punch, not forwards from touchdown, so " +
                    "the turn straddles the settle and the ball reads as driving itself home in one motion instead " +
                    "of landing and then turning. The lead runs back across the WHOLE hop, flight included: 0.12 " +
                    "(the settle) starts the twist at touchdown, and 0.30 (plus the 0.18s flight) starts it as the " +
                    "ball leaves the belt, turning the whole way over. Clamped there.",
                tip: "Want it spinning the moment it jumps? Set it to 0.3 or more.",
                warn: "It was once clamped to the settle alone, so touchdown was the earliest it could fire and " +
                    "anything past 0.12 did nothing. If a build ignores values above 0.12, it predates that fix.",
            },
            {
                label: "Slice Duration", code: "ScrewSliceDuration", kind: "float", default: "0.5", unit: "seconds",
                summary: "How long the slice twist takes.",
                detail: "It deliberately OUTLIVES the settle it straddles — 0.5s against the landing punch’s " +
                    "0.12s — so the scale finishes first and the screw is still turning itself in. Nothing else " +
                    "writes the sprite while a ball sits in a slice, so that tail is safe here.",
                warn: "The DROP twist has no such luxury: it shares the sprite with the falling tumble, which takes " +
                    "over the moment the throw arc ends.",
            },
            {
                label: "Seat Scale Ease", code: "SliceSeatScaleEase", kind: "enum", default: "InOutQuad",
                summary: "The speed shape of the SEAT — the resize from the flight's peak down into the hole.",
                detail: "A dropdown of DOTween curves. InOutSine, the default, lingers at the peak for a frame or " +
                    "two, shrinks fastest through the middle (about 1.5x the average rate) and eases to a stop " +
                    "in the hole — it is the Mathf.SmoothStep this used to hardcode, measured as the closest " +
                    "ease DOTween has and never more than 0.0014 of scale away from it, so picking it changes " +
                    "nothing. Linear shrinks at one rate throughout; the Out family leaves the peak fast and " +
                    "arrives soft; the In family holds the peak and then drops.",
                warn: "A Back or Elastic pick pushes the ball PAST the in-slice size and springs it back. That is " +
                    "the landing overshoot this game deleted on purpose for the screw art — rubber-ball " +
                    "behaviour a driven screw does not have. It is a choice here, not a default.",
            },
            {
                label: "Slice Ease", code: "ScrewSliceEase", kind: "enum", default: "OutExpo",
                summary: "The same, for the twist that seats a ball into a slice.",
                detail: "Separate from the drop’s because they are separate moments — a screw thrown out of a box " +
                    "and one driven into a slice need not bite alike. They shared a curve only because it lived " +
                    "in the code rather than here.",
                warn: "This one eases the WHOLE sweep, and the sweep is the revolutions PLUS the short way round to " +
                    "the slice’s ring facing — so the curve’s slow tail is the approach to that facing, which " +
                    "is what makes the screw settle onto its aim instead of snapping to it. An overshooting pick " +
                    "(Back, Elastic) turns past the facing and comes back.",
            },
            {
                label: "Slice Turns", code: "ScrewSliceTurns", kind: "float", default: "0.75", unit: "revolutions",
                summary: "How far the screw spins as it seats itself into a slice.",
                detail: "Same rule as the drop, with one difference: this twist is normally asked to FINISH on the " +
                    "slice’s ring facing, so the whole turns happen on the way to that angle instead of " +
                    "ending back where they began. 0 turns it off, and the ball is simply aimed instead.",
            },
            {
                label: "Spin Lines Count", code: "ScrewSpinLinesCount", kind: "int", default: "2",
                summary: "How many lines, spaced evenly round the screw.",
                detail: "2 sit opposite each other. 3 reads busier on a small screw.",
            },
            {
                label: "Spin Lines Length", code: "ScrewSpinLinesLength", kind: "float", default: "0.07", unit: "seconds",
                summary: "How much of the screw's recent turn each line draws.",
                detail: "A line is the arc the screw turned through over this many seconds. Longer makes longer " +
                    "lines that also last a little longer, since a slowing screw still covers some angle.",
            },
            {
                label: "Spin Lines Max Arc", code: "ScrewSpinLinesMaxArc", kind: "float", default: "105", unit: "degrees",
                summary: "The longest a line may get.",
                detail: "The twist starts very fast, so without a cap the first frames would draw nearly a full ring.",
            },
            {
                label: "Spin Lines Width", code: "ScrewSpinLinesWidth", kind: "float", default: "0.07", unit: "world units",
                summary: "How thick a line's fill is at its head, its thickest point.",
                detail: "The outline scales with it — its thickness is part of the line art " +
                    "(Assets/Sprites/NonAtlas/ScrewSpinLine*.png, built by tools/gen_spin_line.py). Lines also " +
                    "thin out on their own as they get short, so they shrink as well as shorten.",
            },
            {
                label: "Spin Lines Distance", code: "ScrewSpinLinesDistance", kind: "float", default: "1.03", unit: "x screw radius",
                summary: "How far out from the screw the lines run.",
                detail: "1 puts their centre on the screw's rim; higher moves them out and away from it.",
            },
            {
                label: "Spin Lines Lightness", code: "ScrewSpinLinesLightness", kind: "float", default: "0.05",
                unit: "perceived lightness, 0–1",
                summary: "How much lighter than the screw the lines are filled — the same visible amount for every colour.",
                detail: "Measured as the eye sees lightness (OKLab), with the colour's hue kept, so a red, a green and " +
                    "a blue line all come out equally lighter than their slices. Useful range 0–0.3. The lightest " +
                    "colours (yellow, cream, cyan) have little room below white, so they go at most half the way " +
                    "there and stay close to their slice. Very saturated colours lose some saturation as they " +
                    "lighten, because a screen cannot show them both lighter and as saturated.",
            },
            {
                label: "Spin Lines Fling", code: "ScrewSpinLinesFling", kind: "float", default: "0.3", unit: "x screw radius",
                summary: "How far the lines drift outward as they disappear.",
                detail: "As a line shrinks from the longest it got, it moves out from the screw, as if the turn " +
                    "flung it off — so the effect ends in a throw instead of just stopping. 0.3 means 30% of the " +
                    "radius further out by the time it is gone; 0 shrinks it where it is.",
            },
            {
                label: "Spin Lines Variation", code: "ScrewSpinLinesVariation", kind: "float", default: "0.5", unit: "0–1",
                summary: "How much each line strays on its own, so the lines never turn as one rigid, mirrored set.",
                detail: "At 1 each line may sit up to 25° off the even spacing, be ±20% longer or shorter and ±6% nearer " +
                    "or further, and appear up to two frames after the first; each landing also starts the set at a " +
                    "random angle. The late start does the most to break the mirrored look. 0 = evenly spaced, " +
                    "identical lines.",
            },
            {
                label: "Spin Lines Style", code: "ScrewSpinLinesStyle", kind: "enum", default: "Cartoon",
                summary: "How the spin lines are drawn: as a cartoon line, as light, or both.",
                detail: "Cartoon: flat colour with a dark outline, painted over the slice. Additive: the line is " +
                    "drawn as LIGHT — its colour is added to what is under it, so it can only brighten, and " +
                    "the dark outline is dropped (dark adds nothing). CartoonGlow: the cartoon line with a soft " +
                    "additive halo under it. Additive light washes toward white on light slices (yellow, white) " +
                    "and shows strongest on dark ones (blue, purple).",
            },
            {
                label: "Spin Lines Glow", code: "ScrewSpinLinesGlow", kind: "float", default: "0.4", unit: "0–1",
                summary: "How bright the additive part is.",
                detail: "The whole line in Additive, the halo in CartoonGlow; 0 draws none. Unused in Cartoon.",
            },
            {
                label: "Spin Lines On Twos", code: "ScrewSpinLinesOnTwos", kind: "bool", default: "off",
                summary: "Updates the lines only 30 times a second, like hand-drawn animation.",
                detail: "The screw itself still turns every frame; only the lines hold each pose for two frames. " +
                    "Off draws them smoothly every frame.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "glideBox",
        label: "Glide Box",
        blurb: "A glide box is a normal box in every way except how its screws leave it. Instead of being " +
            "thrown out and falling, each screw floats out on an arc — up past the box, swelling toward the " +
            "camera at the top, then down — turning slowly the whole way with spin lines round it, and lands " +
            "on the funnel floor below the board (or on the screws already lying there). On landing it stops " +
            "turning and rolls on into the throat like any other screw, without the tumble. Because the flight " +
            "is slower than a drop, a box tapped right after a glide box can get its screws onto the belt first.",
        fixed: [
            { label: "Landing spot", value: "found, not fixed", note: "A screw-sized circle is swept down its landing column every step of the flight; it lands where that first rests — funnel floor or pile. Columns over the throat's opening move out onto its shoulder." },
            { label: "Spin after landing", value: "none", note: "The screw keeps the angle it landed on until the belt claims it." },
        ],
        options: [
            {
                label: "Duration", code: "GlideDuration", kind: "float", default: "2.5", unit: "seconds",
                summary: "How long one screw's flight lasts, box to landing.",
                detail: "The whole box takes this plus the release stagger (Ball/Row Delay Frames). A normal drop is about 0.7 s.",
            },
            {
                label: "Arc Height", code: "GlideArcHeight", kind: "float", default: "0.9", unit: "world units",
                summary: "How far the top of the arc rises above the box.",
                detail: "The top is always at the middle of the path (halfway across). Capped so the peak stays on screen for boxes near the top of the board.",
            },
            {
                label: "Peak Scale", code: "GlidePeakScale", kind: "float", default: "1.2", unit: "x in-play size",
                summary: "How big the screw draws at the top of the arc, against a screw in play.",
                detail: "Measured in drawn size (the art's own multiplier included), so 1.2 is exactly 1.2x a normal screw. It swells toward the camera as it rises and shrinks back as it lands — the cannon pull-in's look.",
            },
            {
                label: "Ease", code: "GlideEase", kind: "enum", default: "Linear",
                summary: "The pace along the flight.",
                detail: "Measured along the curve, so Linear moves the screw at one steady speed the whole way: the short climb to the top takes its share of the flight and no more, then the long descent. An Out ease leaves the box fast and floats the landing in; an In ease leaves slowly and lands hard.",
            },
            {
                label: "Spin Speed", code: "GlideSpinSpeed", kind: "float", default: "2", unit: "turns / second",
                summary: "How fast the screw turns in flight.",
                detail: "Clockwise, like the screw twists. A speed, not a count, so changing Duration does not change how fast it turns. 2 is 720° a second; much past 3 the cross starts to strobe.",
            },
            {
                label: "Spin Ease", code: "GlideSpinEase", kind: "enum", default: "Linear",
                summary: "How the turning is spread over the flight.",
                detail: "Linear keeps one speed all the way down and stops dead on landing. An Out ease spins fast out of the box and slows into the landing.",
            },
            {
                label: "Screw Delay Frames", code: "GlideScrewSpawnDelayFrames", kind: "int", default: "3", unit: "fixed steps",
                summary: "Gap between the screws of one row leaving the box.",
            },
            {
                label: "Row Delay Frames", code: "GlideRowSpawnDelayFrames", kind: "int", default: "9", unit: "fixed steps",
                summary: "Gap between the box's rows leaving.",
                detail: "Wider than a normal drop's, so the nine stream out one after another rather than burst.",
            },
            {
                label: "Land Pull", code: "GlideLandPull", kind: "float", default: "0.35", unit: "0–1",
                summary: "How far the landings are pulled from under the box toward the funnel's middle.",
                detail: "0 lands straight below the box; 1 lands everything around the throat.",
            },
            {
                label: "Land Spread", code: "GlideLandSpread", kind: "float", default: "1.1", unit: "screw diameters",
                summary: "How far apart the box's three columns land.",
                detail: "Each row also lands 0.4 diameters to the side of the row below, so no two screws of a box aim at one spot.",
            },
            {
                label: "Land Max X", code: "GlideLandMaxX", kind: "float", default: "4.2", unit: "world units",
                summary: "No landing further out than this from the funnel's centre line.",
                detail: "Keeps them on the open floor under the board rather than against the bowl's side walls.",
            },
            {
                label: "Land Speed", code: "GlideLandSpeed", kind: "float", default: "1", unit: "world units / second",
                summary: "The downward speed handed to physics as the screw touches down.",
                detail: "Small, so it settles onto what it landed on instead of dropping into it.",
            },
            {
                label: "Spin Lines", code: "GlideSpinLinesEnabled", kind: "bool", default: "on",
                summary: "Spin lines round the screw for its whole flight.",
                detail: "Their look (count, width, colour, style) is the Spin Lines knobs under Screw Anims.",
            },
            {
                label: "Spin Lines Length", code: "GlideSpinLinesLength", kind: "float", default: "0.12", unit: "seconds",
                summary: "How much of the screw's recent turn each glide spin line draws.",
                detail: "Longer than the slice twist's because the glide turns far slower — that window would only draw a stub. After landing the lines shrink away over this long.",
            },
            {
                label: "All Boxes Glide (test)", code: "GlideAllBoxes", kind: "bool", default: "off",
                summary: "TEST: every authored box in every level glides, from the next level load.",
                warn: "A local switch for trying the mechanic. Turn it off again — it is saved on the device like every other option.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "splitBox",
        label: "Split Box",
        blurb: "A split box carries three colours in three columns, left to right: its nine screws are three " +
            "per column, each column in its own colour. The lid and tray are drawn from grey sprites through " +
            "each colour's ramp, and where two colours meet they blend over a short band. The closed lid uses " +
            "the even-groove medium set, so its groove keeps one width across the seams. No screw icon on the lid.",
        options: [
            {
                label: "Boundary", code: "SplitBoxBoundary", kind: "float", default: "22.7", unit: "texels",
                summary: "Where the colours change: each boundary sits this far either side of the box's centre.",
                detail: "22.7 is a third of the closed box's width; 18.9 puts it exactly between two screw columns.",
            },
            {
                label: "Blend Width", code: "SplitBoxBlendWidth", kind: "float", default: "0", unit: "texels",
                summary: "How wide the blend between two colours is at each boundary.",
                detail: "0 is a hard edge. The blend keeps an even lightness (it mixes in OKLab).",
            },
            {
                label: "Split Boxes In Threes (test)", code: "SplitBoxesTest", kind: "bool", default: "off",
                summary: "TEST: plain boxes become split boxes in threes, from the next level load.",
                warn: "Three plain boxes A, B, C become A|B|C, B|C|A and C|A|B, so every colour keeps its screw count. A local switch; turn it off again.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "doubleBox",
        label: "Double Box",
        blurb: "A double box is a box inside a box: its frame holds a closed box of another colour, which opens " +
            "and drops like any box. Once that box is gone the double box's own nine screws pop in and it " +
            "plays on as an ordinary open box. Its size and the box inside's place are the art's.",
        options: [
            {
                label: "Screws Pop In", code: "DoubleScrewsPopInDuration", kind: "float", default: "0.22", unit: "seconds",
                summary: "How long the double box's own screws take to pop in once the box inside it has dropped.",
            },
            {
                label: "Double Boxes In Pairs (test)", code: "DoubleBoxesTest", kind: "bool", default: "off",
                summary: "TEST: plain boxes pair up into double boxes, from the next level load.",
                warn: "The first box of each pair holds the second, whose cell is left cleared, so every colour keeps its screw count. A local switch; turn it off again.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "propeller",
        label: "Propeller",
        blurb: "The propeller: a craft tied to the box beside it. Drop that box and it flies to a box on the board " +
            "and blows it up, sending its screws straight to the slices like the magnet. Every box it could " +
            "hit gets a penalty score, and it picks one of the best few at random. Lower score = better " +
            "target. The player can't tap the target while the propeller is flying at it.",
        options: [
            {
                label: "Propellers On Board (test)", code: "PropellersTest", kind: "bool", default: "off",
                summary: "TEST: up to two plain boxes per board become propellers, from the next level load.",
                warn: "Each propeller is tied to a plain box beside it, and its own colour moves inside another plain box as a double box, so every colour keeps its screw count. Ignored while the split or double box test is on. A local switch; turn it off again.",
            },
            {
                label: "Launch: Lift Time", code: "LiftDuration", kind: "float", default: "0.45", unit: "seconds",
                summary: "How long it takes to rise off its base to full height. It already moves off toward its target as it climbs.",
            },
            {
                label: "Launch: Hover Time", code: "HoverDuration", kind: "float", default: "0", unit: "seconds",
                summary: "How long it climbs in place before it moves off toward its target. 0 = it heads off as it rises.",
            },
            {
                label: "Flight: Speed", code: "FlightSpeed", kind: "float", default: "5", unit: "cells per second",
                summary: "How fast it flies to its target, between the shortest and longest times below.",
            },
            {
                label: "Flight: Shortest Time", code: "FlightMinDuration", kind: "float", default: "0.6", unit: "seconds",
                summary: "The flight to a target never takes less than this, however close the target is.",
            },
            {
                label: "Flight: Longest Time", code: "FlightMaxDuration", kind: "float", default: "1.4", unit: "seconds",
                summary: "The flight to a target never takes more than this, however far the target is.",
            },
            {
                label: "Target: Tappable Penalty", code: "TargetPlayable", kind: "int", default: "1000",
                summary: "Added to a box the player could tap right now, so the propeller goes for the boxes the player can't reach first.",
            },
            {
                label: "Target: Front Slice Penalty", code: "TargetFront", kind: "int", default: "200",
                summary: "Added when the slice that wants the box's colour is already at the front of its wedge.",
                detail: "The colour's slice is picked this way: an unblocked slice that isn't full first, the front one first. Only when no unblocked slice is left does a blocked (frozen) one count.",
            },
            {
                label: "Target: Blocked Slice Penalty", code: "TargetBlocked", kind: "int", default: "180",
                summary: "Added when only frozen slices still want the box's colour.",
                detail: "Adds to the front penalty when that frozen slice is at the front (200 + 180 = 380 with the defaults). A mystery slice is not blocked: it counts by its real colour.",
            },
            {
                label: "Target: No Slice Penalty", code: "TargetNoSlice", kind: "int", default: "100",
                summary: "Added when no slice wants the box's colour any more.",
            },
            {
                label: "Target: Condition Penalty", code: "TargetCondition", kind: "int", default: "10",
                summary: "Added once for each of: an arrow still locked, a link, a multiplier below the box in its column.",
            },
            {
                label: "Target: Pool Size", code: "TargetPoolSize", kind: "int", default: "3",
                summary: "How many of the best-scoring boxes the propeller picks from, at random. 1 = always the best one.",
                warn: "The pool is the first N boxes by score, whatever their scores are. With few good boxes, tappable ones get into it.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "screwShadow",
        label: "Screw Shadow",
        blurb: "The shadow under a screw. Ball.prefab decides what that shadow IS — the Shadow child is " +
            "authored at a distance below the ball and at a size — and everything here MULTIPLIES those, " +
            "one pair of numbers per stage of a ball's life. 1 leaves the art exactly as authored, 0 means " +
            "no drop at all (distance) or no shadow at all (scale). Distance is what sells HEIGHT: a shadow " +
            "far from its ball reads as a ball in the air, and one tight underneath reads as a ball resting " +
            "on something.",
        options: [
            {
                label: "In Box Distance", code: "ScrewShadowInBoxDistance", kind: "float", default: "1",
                unit: "x authored",
                summary: "How far the shadow sits below a screw still waiting in its box.",
                detail: "Boxed screws are not in the air, so this is normally 1 — the authored distance, which is " +
                    "small. Raising it lifts every screw on the board off its tray at once, which is a very " +
                    "different look from lifting one that has been thrown.",
            },
            {
                label: "In Ego Box Distance", code: "ScrewShadowInEgoBoxDistance", kind: "float", default: "1", unit: "x authored",
                summary: "How far the shadow sits below a screw sitting in an ego rescue box.",
                detail: "The same as the matching In Box value, but only for screws the ego revive collected into a " +
                    "rescue box on the ego strip, so that strip can read differently from the board. Starts as " +
                    "a copy of In Box.",
            },
            {
                label: "Ego Drop Distance", code: "ScrewShadowEgoDropDistance", kind: "float", default: "2", unit: "x authored",
                summary: "How far the shadow falls behind a screw thrown from an ego rescue box.",
                detail: "A screw thrown from the ego strip uses this one flat value from the throw until the first " +
                    "thing it touches, instead of the board's Drop min/max height ramp, and never runs the " +
                    "Drop Settle. A collision or belt claim moves it on to the Gameplay values as usual.",
            },
            {
                label: "Drop Distance Min", code: "ScrewShadowDropDistanceMin", kind: "float", default: "4",
                unit: "x authored",
                summary: "How far the shadow falls behind a screw in the air at the board's BOTTOM row (and below).",
                detail: "The stage the whole feature exists for. It starts the moment the screw is thrown and ends " +
                    "at the first thing it touches — a wall, the funnel, another screw, or the belt claiming " +
                    "it out of the air. The bigger the number the higher the throw reads. The distance follows the " +
                    "screw's live height: linear from this at the bottom row up to Drop Distance Max at the 7th, " +
                    "so the shadow closes in as the screw falls.",
                tip: "Tune this against the authored offset rather than instead of it: this is a multiplier, so " +
                    "a tiny authored distance needs a large number here to travel anywhere.",
            },
            {
                label: "Drop Distance Max", code: "ScrewShadowDropDistanceMax", kind: "float", default: "8",
                unit: "x authored",
                summary: "How far the shadow falls behind a screw in the air at the board's 7th row (and above).",
                detail: "The top of the height ramp that starts at Drop Distance Min on the bottom row, measured " +
                    "between row centres. A screw higher up the board reads as higher; anything above the 7th " +
                    "row stays at this value.",
            },
            {
                label: "Gameplay Distance", code: "ScrewShadowGameplayDistance", kind: "float", default: "1",
                unit: "x authored",
                summary: "How far the shadow sits below a screw that is down and in play.",
                detail: "Everything after the landing and before a slice: rolling through the funnel, queued in the " +
                    "holder, riding the belt. 1 is the authored distance, which is what the art was drawn for.",
            },
            {
                label: "In Slice Distance", code: "ScrewShadowInSliceDistance", kind: "float", default: "0",
                unit: "x authored",
                summary: "How far the shadow sits below a screw seated in a slice.",
                detail: "0 closes the drop completely, so the shadow is concentric with the screw rather than below " +
                    "it. A seated screw is resting IN a socket, and a shadow offset below it would read as the " +
                    "screw floating over the wedge.",
            },
            {
                label: "Slice Hop Distance", code: "ScrewShadowSliceHopDistance", kind: "float", default: "6",
                unit: "x authored",
                summary: "How far the shadow drops while a screw hops off the belt into a slice.",
                detail: "The hop is the game's other airborne moment: the screw leaves the belt, arcs up to a peak " +
                    "and drops into the hole. This is what that peak reads as. It is a separate number from the " +
                    "throw's because the two arcs are nothing alike — a throw crosses the board, a hop crosses a " +
                    "few centimetres — and one value could not flatter both.",
            },
            {
                label: "Slice Hop Scale", code: "ScrewShadowSliceHopScale", kind: "float", default: "1",
                unit: "x authored",
                summary: "How big the shadow draws through that hop.",
                detail: "1 keeps the shadow at its authored size for the whole arc, so only the distance carries the " +
                    "lift. Note the screw itself SWELLS through the hop (see the Slices rows), and the shadow " +
                    "hangs off the ball, so it already grows with it — this is on top of that.",
            },
            {
                label: "In Box Scale", code: "ScrewShadowInBoxScale", kind: "float", default: "1",
                unit: "x authored",
                summary: "How big the shadow draws under a screw waiting in its box.",
                detail: "Multiplies the size the Shadow child is authored at, on top of whatever scale the ball " +
                    "itself is wearing — so a boxed screw's shadow is already small because the screw is.",
            },
            {
                label: "In Ego Box Scale", code: "ScrewShadowInEgoBoxScale", kind: "float", default: "1", unit: "x authored",
                summary: "How big the shadow draws for a screw sitting in an ego rescue box.",
                detail: "The same as the matching In Box value, but only for screws the ego revive collected into a " +
                    "rescue box on the ego strip, so that strip can read differently from the board. Starts as " +
                    "a copy of In Box.",
            },
            {
                label: "Ego Drop Scale", code: "ScrewShadowEgoDropScale", kind: "float", default: "1", unit: "x authored",
                summary: "How big the shadow draws for a screw thrown from an ego rescue box.",
                detail: "A screw thrown from the ego strip uses this one flat value from the throw until the first " +
                    "thing it touches, instead of the board's Drop min/max height ramp, and never runs the " +
                    "Drop Settle. A collision or belt claim moves it on to the Gameplay values as usual.",
            },
            {
                label: "Drop Scale", code: "ScrewShadowDropScale", kind: "float", default: "1",
                unit: "x authored",
                summary: "How big the shadow draws while the screw is in the air.",
                detail: "Left at 1 the shadow keeps its size the whole way down and only the distance sells the " +
                    "height. Under 1 it shrinks as it falls behind, which reads as the ball getting further " +
                    "from the board rather than just further from its shadow.",
            },
            {
                label: "Gameplay Scale", code: "ScrewShadowGameplayScale", kind: "float", default: "1",
                unit: "x authored",
                summary: "How big the shadow draws once the screw is down and in play.",
            },
            {
                label: "In Slice Scale", code: "ScrewShadowInSliceScale", kind: "float", default: "1",
                unit: "x authored",
                summary: "How big the shadow draws under a screw seated in a slice.",
                detail: "0 takes it away entirely, which is the point: the slice art already carries a carved socket " +
                    "and its own shading, so a second shadow on top of it is one shadow too many.",
            },
            {
                label: "In Box Alpha", code: "ScrewShadowInBoxAlpha", kind: "float", default: "0.65", unit: "x authored",
                summary: "Shadow opacity while the ball is sitting in its box.",
                detail: "A factor on the alpha the sprite is drawn with, not a replacement for it — 1 is the art " +
                    "exactly as authored and 0 is invisible. Blends to the next point's alpha on the same tween " +
                    "as distance and size; in the air it follows the screw's height between Drop Alpha Min " +
                    "and Max just as the distance does.",
            },
            {
                label: "In Ego Box Alpha", code: "ScrewShadowInEgoBoxAlpha", kind: "float", default: "0.65", unit: "x authored",
                summary: "Shadow opacity for a screw sitting in an ego rescue box.",
                detail: "The same as the matching In Box value, but only for screws the ego revive collected into a " +
                    "rescue box on the ego strip, so that strip can read differently from the board. Starts as " +
                    "a copy of In Box.",
            },
            {
                label: "Ego Drop Alpha", code: "ScrewShadowEgoDropAlpha", kind: "float", default: "0.55", unit: "x authored",
                summary: "Shadow opacity for a screw thrown from an ego rescue box.",
                detail: "A screw thrown from the ego strip uses this one flat value from the throw until the first " +
                    "thing it touches, instead of the board's Drop min/max height ramp, and never runs the " +
                    "Drop Settle. A collision or belt claim moves it on to the Gameplay values as usual.",
            },
            {
                label: "Drop Alpha Min", code: "ScrewShadowDropAlphaMin", kind: "float", default: "0.5", unit: "x authored",
                summary: "Shadow opacity while the ball is in the air at (or below) the board's bottom row.",
                detail: "A factor on the alpha the sprite is drawn with, not a replacement for it — 1 is the art " +
                    "exactly as authored and 0 is invisible. Blends to the next point's alpha on the same tween " +
                    "as distance and size; in the air it follows the screw's height between Drop Alpha Min " +
                    "and Max just as the distance does.",
            },
            {
                label: "Drop Alpha Max", code: "ScrewShadowDropAlphaMax", kind: "float", default: "0.3", unit: "x authored",
                summary: "Shadow opacity while the ball is in the air at (or above) the board's 7th row.",
                detail: "A factor on the alpha the sprite is drawn with, not a replacement for it — 1 is the art " +
                    "exactly as authored and 0 is invisible. Blends to the next point's alpha on the same tween " +
                    "as distance and size; in the air it follows the screw's height between Drop Alpha Min " +
                    "and Max just as the distance does.",
            },
            {
                label: "Gameplay Alpha", code: "ScrewShadowGameplayAlpha", kind: "float", default: "0.55", unit: "x authored",
                summary: "Shadow opacity while the ball is down and in play: the funnel, the holder, the belt.",
                detail: "A factor on the alpha the sprite is drawn with, not a replacement for it — 1 is the art " +
                    "exactly as authored and 0 is invisible. Blends to the next point's alpha on the same tween " +
                    "as distance and size; in the air it follows the screw's height between Drop Alpha Min " +
                    "and Max just as the distance does.",
            },
            {
                label: "In Slice Alpha", code: "ScrewShadowInSliceAlpha", kind: "float", default: "0.4", unit: "x authored",
                summary: "Shadow opacity while the ball is seated in a slice socket.",
                detail: "A factor on the alpha the sprite is drawn with, not a replacement for it — 1 is the art " +
                    "exactly as authored and 0 is invisible. Blends to the next point's alpha on the same tween " +
                    "as distance and size; in the air it follows the screw's height between Drop Alpha Min " +
                    "and Max just as the distance does.",
            },
            {
                label: "Slice Hop Alpha", code: "ScrewShadowSliceHopAlpha", kind: "float", default: "0.4", unit: "x authored",
                summary: "Shadow opacity while the ball hops off the belt into a slice.",
                detail: "A factor on the alpha the sprite is drawn with, not a replacement for it — 1 is the art " +
                    "exactly as authored and 0 is invisible. Blends to the next point's alpha on the same tween " +
                    "as distance and size; in the air it follows the screw's height between Drop Alpha Min " +
                    "and Max just as the distance does.",
            },
            {
                label: "Drop Settle Duration", code: "ScrewShadowDropSettleDuration", kind: "float", default: "0.5", unit: "seconds",
                summary: "How long a falling screw's shadow takes to reach the Gameplay point once it falls past the bottom row.",
                detail: "A thrown screw that falls below the board's bottom row without having touched anything yet " +
                    "stops reading its height and blends distance, size and alpha from wherever they are to the " +
                    "Gameplay values over this long. A collision (or a belt claim) cuts it short with the normal " +
                    "Tween Duration. 0 snaps.",
            },
            {
                label: "Tween Duration", code: "ScrewShadowTweenDuration", kind: "float", default: "0.1",
                unit: "seconds",
                summary: "How long the shadow takes to travel between two stages.",
                detail: "Linear (except the close into a slice — see In Slice Ease), and distance and size ride the one clock so they can never drift apart. It matters " +
                    "most leaving a box: a screw is thrown in a single frame, and a shadow that jumps its whole " +
                    "drop in that frame reads as a glitch rather than as lift-off.",
                warn: "0 snaps, which is only worth having to see where a stage change actually happens.",
            },
            {
                label: "In Slice Ease", code: "ScrewShadowInSliceEase", kind: "enum", default: "InOutQuad",
                summary: "The curve the shadow follows as it closes into a slice.",
                detail: "A dropdown of DOTween easing curves for the last stage change of a hop: Slice Hop to In " +
                    "Slice, over Tween Duration. Distance, size and alpha all follow it together. The default " +
                    "is In Out Quad; Linear is the original. \"Out\" curves fade most of the shadow early; \"In\" curves hold it and " +
                    "drop it just before the screw seats. Every other stage change stays linear.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "conveyor",
        label: "Wheel",
        blurb: "The turning ring the balls queue on. Balls enter it from the holder at the bottom, ride round, and " +
            "hop off into a matching slice.",
        fixed: [
            {
                label: "SlotCount", value: "30",
                note: "How many evenly-spaced places there are on the ring. Fixed in code (WheelOptions.SlotCount), " +
                    "not tunable at runtime — the slots are built once when the level loads.",
            },
        ],
        options: [
            {
                label: "Holes", code: "WheelHoles", kind: "bool", default: "on",
                summary: "Which belt artwork is shown: the one with hole sockets drawn on it, or the plain one.",
                detail: "Artwork only. The balls still ride the same slots either way — turning it off just stops drawing sockets underneath them.",
            },
            {
                label: "Arrow Alpha", code: "WheelArrowAlpha", kind: "float", default: "0.58",
                summary: "Opacity of the six direction arrows painted on the belt (0 hides them, 1 is the art as authored).",
                detail: "The arrows are children of the belt sprite, so they always turn with it \u2014 this only " +
                    "fades them. Applied to the renderers the Conveyor holds a list of; a change-guarded " +
                    "write, so dragging it costs nothing per frame.",
            },
            {
                label: "Shadow Strength", code: "WheelShadowStrength", kind: "float", default: "0.8",
                figure: "ring",
                summary: "How dark the drop shadows under the slices read (0 hides them, 1 is the art as drawn).",
                detail: "SliceShadows mirrors every live slice's shadow one sorting order below it, combined " +
                    "per order with the other shadows so overlaps take the darkest instead of stacking; " +
                    "this is the shadow's opacity in that mix — the blur itself is " +
                    "never touched, so the falloff stays exactly as authored and only its weight changes. " +
                    "The art is drawn at full strength on purpose: it is easier to take a shadow down than " +
                    "to redraw a blur. 0.8 is the value the slice mesh was judged at in the generator the " +
                    "current slice sprites were rendered from. Read once per frame, so dragging this shows " +
                    "the result live.",
            },
            {
                label: "AO Strength", code: "WheelAOStrength", kind: "float", default: "0.3",
                figure: "ring",
                summary: "How dark the ambient occlusion reads where the slices' walls meet the ground (0 hides it, 1 is the art as drawn).",
                detail: "SliceAO mirrors every live slice with a pooled contact-shadow sprite - its base dropped " +
                    "by the wall depth and blurred - drawn BELOW every slice, so it only shows on bare " +
                    "ground (a neighbouring slice covers it, as it covers that wall). This is multiplied " +
                    "into the sprite's alpha; the art is full strength. 0.3 is the Ground AO strength the " +
                    "slice mesh generator shows by default. Read once per frame, so dragging this shows " +
                    "the result live.",
            },
            {
                label: "AO Lift Fade", code: "WheelAOLiftFade", kind: "float", default: "0.1", unit: "world units",
                figure: "ring",
                summary: "How high a slice has to hop before its ground AO is gone (0 = gone the moment it leaves the ground).",
                detail: "While a slice is lifted (the promote hop, the shuffle hop - Slice.LiftY), SliceAO fades " +
                    "its ground AO as (1 - lift / this)^2: fastest as the gap first opens, the way a contact " +
                    "shadow thins. 0.1 sits just under both hops' 0.12 peak, so the AO is gone over the top " +
                    "of every jump and comes back as the slice lands. The land bump dips DOWN and leaves it " +
                    "at full.",
            },
            {
                label: "Brake Duration", code: "WheelBrakeDuration", kind: "float", default: "2", unit: "sec",
                figure: "ring",
                summary: "How long the belt takes to coast to a halt once the level is decided.",
                detail: "The belt does not cut out when a level ends — it slows to a stop over this span. " +
                    "Applies to every ending: a win, a deadlock and a quit. The same span spins it back " +
                    "UP if an ego revive hands play back, since a continue resumes on the same board rather " +
                    "than reloading. 0 stops it dead on the frame the outcome lands. Brake Ease decides the " +
                    "shape of the slowdown; this decides how long it lasts.",
            },
            {
                label: "Brake Ease", code: "WheelBrakeEase", kind: "enum", default: "InOutSine",
                figure: "ring",
                summary: "The shape of that slowdown — how the belt sheds its speed across the brake.",
                detail: "A dropdown of DOTween easing curves, applied to the belt's SPEED rather than to a " +
                    "position, so the curve is literally the deceleration. InOutSine (the default) is flat " +
                    "at both ends: the belt eases into the brake, loses most of its speed through the middle " +
                    "and settles onto zero instead of arriving with a jolt, the way a machine being switched " +
                    "off winds down. Linear is a constant deceleration and stops dead at the last instant; " +
                    "\"In\" curves hold speed and then drop away; \"Out\" curves brake hard immediately and " +
                    "then creep to a halt.",
                tip: "One curve serves both directions — an ego revive walks the same phase back down, so an " +
                    "asymmetric pick makes the spin-up the mirror in time of the brake. Overshooting curves " +
                    "(the Back and Elastic families) are clamped at a standstill: the belt never runs backwards.",
            },
            {
                label: "Dot Empty Threshold", code: "WheelDotEmptyThreshold", kind: "int", default: "6",
                figure: "ring",
                summary: "The belt's slot dots show while FEWER than this many slots are free.",
                detail: "A small dot marks every belt slot, but only when the belt is nearly full — a warning that " +
                    "room is running out. At 6 they appear with 5 free slots or less, and fade away again once " +
                    "6 or more are free. For this count a slot is free unless a ball is SETTLED in it — one still " +
                    "dropping or sliding in, or sliding or hopping away, doesn't take it. The dots draw under the " +
                    "balls, so a slot with a ball in it just has its dot covered.",
                tip: "0 never shows them; 31 or more shows them all the time.",
            },
            {
                label: "Dot Fade In Duration", code: "WheelDotFadeInDuration", kind: "float", default: "0.3", unit: "sec",
                figure: "ring",
                summary: "How long a dot takes to fade in.",
            },
            {
                label: "Dot Fade In Ease", code: "WheelDotFadeInEase", kind: "enum", default: "OutCubic",
                figure: "ring",
                summary: "The shape of the fade in.",
            },
            {
                label: "Dot Fade Out Duration", code: "WheelDotFadeOutDuration", kind: "float", default: "0.15", unit: "sec",
                figure: "ring",
                summary: "How long a dot takes to fade out.",
            },
            {
                label: "Dot Fade Out Ease", code: "WheelDotFadeOutEase", kind: "enum", default: "OutQuad",
                figure: "ring",
                summary: "The shape of the fade out.",
            },
            {
                label: "Endgame Speed Up", code: "WheelEndgameSpeedUpEnabled", kind: "bool", default: "off",
                summary: "Whether the game speeds up once the level can no longer be lost.",
                detail: "Off (the default): when the win is certain the game keeps its normal speed — Tap To Skip is " +
                    "the way to hurry it along — and the belt keeps its normal slide pace. On: the whole game " +
                    "clock speeds up (by 1.5 through level 12, 1.45 on levels 13-14, 1.4 from level 15 on — set " +
                    "in code, not on the panel), and slides switch to Endgame Shift Duration / Endgame Initial " +
                    "Shift Duration so they don't turn frantic under the faster clock.",
            },
            {
                label: "Position Jitter Radius", code: "WheelJitterRadius", kind: "float", default: "0.1", unit: "world units",
                summary: "A tiny random offset per ball so the queue looks natural instead of laser-straight.",
                detail: "Picked once when a ball joins the belt and kept, so it doesn't shimmer.",
                tip: "0 gives a perfectly regimented ring, which looks mechanical but is great for debugging.",
            },
            {
                label: "Hole Jump Duration", code: "HoleJumpDuration", kind: "float", default: "0.18", unit: "seconds",
                figure: "hop",
                summary: "How long a ball's move onto the belt — and off it into a slice — aims to take.",
                detail: "Off the belt it's exact: the slice hop always takes this long. Onto the belt it's the " +
                    "TARGET: a waiting ball drops straight down its own column, and the game picks whichever " +
                    "empty hole turns under that spot closest to this time — so the real fall can run up to " +
                    "half a slot's turn (about 0.085s at default speed) longer or shorter.",
                warn: "This one duration covers hops of very different lengths, so a ball reaching for a far slice " +
                    "hole visibly flies faster than one hopping into the nearest slot.",
            },
            {
                label: "Entrance Feed", code: "EntranceFeedEnabled", kind: "bool", default: "on",
                summary: "Look-ahead belt feed: claim balls still falling through the entrance zone.",
                detail: "Without it only balls physically touching the holder can be handed to the belt, so a " +
                    "ball resting on top of a parked one waits a whole settle cycle even when empty holes " +
                    "are passing. With it, the holder also watches the entrance zone above and claims " +
                    "committed fallers for holes their column can catch — held balls always get first pick, " +
                    "lanes with anything below stay untouched, and sideways movers are left physical.",
            },
            {
                label: "Entrance Magnet Dive Factor", code: "EntranceMagnetDiveFactor", kind: "float", default: "0.5",
                summary: "How much earlier than its natural fall a ball may catch a hole.",
                detail: "The floor of the magnet window, as a fraction of the ball's natural arrival time: at " +
                    "0.5 a hole arriving in no less than 50% of the natural fall time is catchable, and " +
                    "the ball dives (accelerates past gravity, at most twice as fast here) to meet it. A hole running ahead of a ball " +
                    "STAYS ahead forever (both countdowns shrink at one second per second), so without " +
                    "this early reach those slots always passed unclaimed.",
                tip: "Lower = stronger magnet, harder dives. 1 = no magnet: only naturally-aligned holes.",
            },
            {
                label: "Entrance Max Sideways Speed", code: "EntranceMaxSidewaysSpeed", kind: "float", default: "10", unit: "world units/s",
                summary: "Fastest sideways drift a faller may have and still be claimed.",
                detail: "Above this sideways speed a ball stays physical until it straightens out. With " +
                    "Entrance Keep Sideways Motion off, claiming pins the ball's X for the whole drop, so " +
                    "a ball still ricocheting off the funnel walls would suddenly fall unnaturally straight " +
                    "— keep this low. With it on, the drift carries into the drop, so this can go much " +
                    "higher.",
                warn: "Balls usually come through the funnel diagonally at 2–9 units/s. At a low limit most of " +
                    "them can't be claimed in the air, and an empty slot passing under one can cost a full " +
                    "lap of the belt when it's nearly full.",
            },
            {
                label: "Entrance Keep Sideways Motion", code: "EntranceKeepSidewaysMotion", kind: "bool", default: "on",
                summary: "A ball claimed in the air keeps drifting sideways and eases to a stop over its slot.",
                detail: "On: the claimed ball carries its sideways speed into the drop and slows it evenly to zero " +
                    "by touchdown, curving onto the slot — the game picks the hole by where that drift ends, " +
                    "kept clear of the funnel walls. Off: the ball stops dead sideways and drops straight " +
                    "down its current column, which reads as a snap on fast diagonal fallers.",
                tip: "Pair with a higher Entrance Max Sideways Speed — that's what lets diagonal fallers be claimed at all.",
            },
            {
                label: "Entrance Lane Clear Margin", code: "EntranceLaneClearMargin", kind: "float", default: "0", unit: "seconds",
                summary: "How long a ball that just dropped onto the belt keeps the lane above it closed.",
                detail: "When two balls fall down the same lane one after the other, the upper one can only be " +
                    "claimed for a hole it reaches after the lower one has landed, plus this many seconds. At " +
                    "0 it simply lands right after: the two take different slots a slot apart, so they can't " +
                    "end up on top of each other. A margin makes the second ball miss the slot right behind " +
                    "the first — on a nearly full belt that costs a whole lap.",
                tip: "Raise it only if two balls landing back to back into neighbouring slots looks too tight.",
            },
            {
                label: "Funnel Pull Strength", code: "FunnelPullStrength", kind: "float", default: "15", unit: "world units/s²",
                summary: "Pulls loose balls in the funnel toward the drop point while an empty belt slot is coming.",
                detail: "Balls queued on a funnel wall slide in one after another, and the last of the line can reach " +
                    "the drop point just after the belt's only empty slot has gone by — then it waits a whole lap. " +
                    "While an empty slot is under a second away, every free ball within Funnel Pull Radius above " +
                    "the drop point is accelerated toward it by this much. It's a real physics push, so walls and " +
                    "other balls still stop it: a line on a wall just slides in faster, together. Gravity is 50 " +
                    "for comparison. 0 turns it off.",
                warn: "Too strong reads as the funnel sucking balls in, and can pile them up at the throat.",
            },
            {
                label: "Funnel Pull Strength Last Slots", code: "FunnelPullStrengthLastSlots", kind: "float", default: "30", unit: "world units/s²",
                summary: "Funnel Pull Strength to use while the belt has 2 or fewer empty slots left.",
                detail: "With only a couple of gaps left, a ball that reaches the throat just after one has passed waits " +
                    "a whole lap for the next. A stronger pull in just those moments gets balls sliding off the " +
                    "funnel walls to the throat a beat sooner — still a physics push, so they simply slide faster. " +
                    "0 keeps Funnel Pull Strength all the time.",
                tip: "Try 30, then 40 (gravity is 50).",
            },
            {
                label: "Funnel Pull Radius", code: "FunnelPullRadius", kind: "float", default: "2.5", unit: "world units",
                summary: "How far above the drop point the funnel pull reaches.",
            },
            {
                label: "Screw Hit Speed Cap", code: "ScrewHitSpeedCap", kind: "float", default: "3", unit: "world units/s",
                summary: "Limits how fast a ball comes away from bumping a ball that's dropping into its slot or parked on the holder.",
                detail: "Those two are moved by script, and the physics treats them as infinitely heavy: a ball that " +
                    "touches one gets flung at full speed, out of the throat and up the far wall. After such a " +
                    "contact the free ball's sideways and upward speed are capped at this, so it still visibly " +
                    "gets pushed but drops back instead of flying. Its fall is never capped. Bumps between two " +
                    "free balls are left alone. 0 turns it off.",
                tip: "Try 3, 2, 1 — lower settles faster but can look like the ball sticks to the one it touched.",
            },
            {
                label: "Throat Outward Speed Cap", code: "ThroatOutwardSpeedCap", kind: "float", default: "2", unit: "world units/s",
                summary: "Stops balls from shooting back out of the throat, without slowing the ones coming in.",
                detail: "Balls leave the throat two ways: sliding fast off one funnel wall straight across and up the " +
                    "other, or knocked out by a ball coming the other way. Both are a ball inside the throat moving " +
                    "away from its centre, so only that sideways speed is capped. A ball heading into the throat is " +
                    "never touched, so the pour keeps its pace. 0 turns it off.",
                tip: "Try 2, then 1.",
            },
            {
                label: "Throat Outward Cap Radius", code: "ThroatOutwardCapRadius", kind: "float", default: "1.5", unit: "world units",
                summary: "How far around the top of the throat the outward cap reaches, besides the throat itself.",
                detail: "Most flights across start at the throat's mouth and lips, where the waiting pile gets hit by " +
                    "balls falling or sliding in fast. The cap covers the throat below its top plus a circle of this " +
                    "radius around its top centre.",
            },
            {
                label: "Approach Outward Speed Cap", code: "ApproachOutwardSpeedCap", kind: "float", default: "2", unit: "world units/s",
                summary: "Stops a screw knocked back up the funnel wall on landing from flying far away from the throat.",
                detail: "A box's screws land on the funnel wall as a 3x3 block. Each one comes down on the shoulder of " +
                    "the screw below it, which is already sliding toward the throat, and gets thrown back up the " +
                    "wall. The outermost one has nothing beside it to stop it, flies far back and reaches the " +
                    "throat last, leaving empty slots behind the pour. From the moment a screw is live until it " +
                    "first reaches the throat, its sideways speed away from the throat is capped at this. It still " +
                    "visibly bounces back, just not as far. Speed toward the throat is never touched. 0 turns it off.",
                tip: "Try 2, then 1.",
            },
            {
                label: "Hole Jump Height", code: "HoleJumpHeight", kind: "float", default: "0.4", unit: "world units",
                summary: "How high that hop arcs.",
                tip: "0 makes balls slide flatly instead of hopping, which reads as sliding on ice.",
            },
            {
                label: "Hole Jump Ease", code: "HoleJumpEase", kind: "enum", default: "OutSine",
                summary: "The speed shape of the hop into a slice — constant, fast off the belt, fast into the slice, or fast at both ends.",
                detail: "A dropdown of DOTween easing curves remapping the flight's clock into its progress. " +
                    "OutQuad (the default) leaves the belt fast and eases into the landing, where the " +
                    "scale punch supplies the accent; Linear is the old constant speed. \"In\" curves " +
                    "wind up slowly and strike the slice fast; \"OutIn\" curves rush both ends and hang " +
                    "in the middle. The arc height and landing scale ride the same shape, so the whole " +
                    "hop speeds and slows as one.",
                tip: "Only the flight into a slice (cannon/magnet shots included) — the drop onto the belt keeps its physics-matched fall.",
            },
            {
                label: "Shift Smooth Start Stop", code: "ShiftSmoothStartStop", kind: "bool", default: "on",
                summary: "Balls ease into a slide and ease into their slot at the end of it, every time.",
                detail: "On: a ball setting off from its slot always runs the warm-up (two slides, 0.78 s then 0.63 s) from zero, " +
                    "and the slide that ends a run (the slot ahead is taken) slows to rest in its slot. " +
                    "Off: the warm-up only runs on a ball's first slides after it lands on the belt — later " +
                    "set-offs launch at full cruise pace (0.48 s a slot) in a single frame — and every slide arrives at " +
                    "speed, so a run halts dead the moment it reaches the ball ahead.",
                warn: "A ball easing to a stop behind one that then starts moving a moment later brakes and " +
                    "re-accelerates — a small traffic-like wave through the queue.",
            },
            {
                label: "Shift Smooth Follow", code: "ShiftSmoothFollow", kind: "bool", default: "on",
                summary: "A ball setting off right behind a sliding ball eases into its pace instead of jumping to it.",
                detail: "So two neighbours never overlap, a ball that starts sliding right behind one already sliding " +
                    "follows the leader's motion. Off: it takes on the leader's speed the instant it starts — a small " +
                    "jump when their speeds differ. On: it keeps its own speed and blends into the leader's over the " +
                    "slide. Needs Shift Smooth Start Stop.",
            },
            {
                label: "Feed Lookahead Radius", code: "FeedLookaheadRadius", kind: "float", default: "2.5", unit: "world units",
                summary: "While nothing waits at the throat, balls coming down within this distance of it count as waiting too.",
                detail: "Normally only balls at the throat count as waiting, so a ball still sliding down the funnel " +
                    "is ignored: a train behind an empty slot slides in and closes it, and the ball arrives just " +
                    "in time to wait for that train. With a radius, a ball within it holds the empty slots it can " +
                    "still reach (judged by its distance), so the gap comes to the throat and the ball drops in. " +
                    "Balls already at the throat are not affected. 0 turns it off. Measured on levels 21-32 with the mouth " +
                    "empty, a ball takes about 0.3 s to reach the throat from 2 units, 0.4 s from 2.5 and 0.6 s from 3.",
                warn: "Too large counts balls that arrive late: the train waits, the gap passes the throat empty, and the train closes it afterwards.",
            },
            {
                label: "Shift Finish Runs", code: "ShiftFinishRuns", kind: "bool", default: "on",
                summary: "A train of balls that starts sliding to close a gap always finishes, instead of braking halfway near the throat.",
                detail: "While a ball waits at the throat, the empty slots about to pass it are kept free for it, so the " +
                    "belt can't slide balls into them. Off: that stops a train wherever it is, and it visibly brakes " +
                    "mid-gap for no reason. On: a train already sliding finishes its run, and a train at rest only " +
                    "sets off if it can close the gap before any of it has to be kept free; otherwise it stays put " +
                    "and the gap passes the throat for the waiting ball.",
            },
            {
                label: "Pack Toward Biggest Gap", code: "PackTowardBiggestGap", kind: "bool", default: "on",
                summary: "When the belt closes a gap, no ball travels further than it has to.",
                detail: "Balls on the belt only slide forward, and the belt packs them into one unbroken line around the " +
                    "ball that stays put. Off: it picks the plan with the fewest slides in total — so with a long " +
                    "train and two balls a couple of slots ahead of it, those two race forward all the way round the " +
                    "empty belt to the train's tail. On: the ball with the most empty belt ahead of it stays put, so " +
                    "the long train slides the couple of slots up to the two instead.",
            },
            {
                label: "Endgame Shift Duration", code: "WheelEndgameShiftDuration", kind: "float", default: "0.2", unit: "seconds",
                figure: "shift",
                summary: "A slide's duration while the endgame speed-up runs (Endgame Speed Up on; in game time, which it speeds up).",
                detail: "The endgame drain speeds up the game clock (1.4-1.5x by level), which would speed up the slide " +
                    "pace with it. While the drain is on, slides cruise at this duration instead of the usual " +
                    "0.48 s: at the defaults (0.2 against a 2x clock) a slide takes ~0.1s of real time — " +
                    "well over twice as quick as normal play.\n\n" +
                    "The switch is instant but downward-only: when the drain begins, a ball already sliding " +
                    "faster than this pace is capped to it on the spot (position stays continuous, the speed " +
                    "steps down); a ball sliding slower just keeps ramping as it was.",
            },
            {
                label: "Endgame Initial Shift Duration", code: "WheelEndgameInitialShiftDuration", kind: "float", default: "0.4", unit: "seconds",
                figure: "shift",
                summary: "A fresh ride's first slide duration while the endgame speed-up runs (Endgame Speed Up on).",
                detail: "The set-off ramp's starting duration during the drain: a fresh ride ramps from here down " +
                    "to Endgame Shift Duration. In practice it's rarely seen — the drain only starts once every " +
                    "ball is claimed, so new belt rides can barely happen — but the ramp needs a consistent " +
                    "pair to compute from.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "sliceArt",
        label: "Slice Art",
        blurb: "Which cut of the conveyor's wedge art the game wears. One set is in the project now, but the machinery holds as many as you " +
            "like side by side, so a new look can be held against the one it would replace on the " +
            "same board, in the same level, without a rebuild. That is how the current one was " +
            "picked: two earlier cuts sat here until it won, and were then taken out.",
        fixed: [
            {
                label: "What differs",
                value: "4 colourless wedges + 1 socket overlay",
                note: "A set is one grayscale shading sprite per stage, tinted to the palette by the renderer " +
                    "\u2014 there is no per-colour art any more. The active stage has a second sprite " +
                    "holding just the spherical seats of its three sockets, on its own renderer and " +
                    "in its own colour, measured off the original hand-drawn art. The lip around " +
                    "each seat stays the piece colour. The mystery wedges and the drop shadows are " +
                    "shared by every set and never change when you switch.",
            },
            {
                label: "Where they live",
                value: "Assets/Sprites/Slices/MeshTight",
                note: "MeshTight is addressed \u201cSlicesMeshTight/\u2026\u201d. The shared shadow and mystery " +
                    "wedges stay at \u201cSlices/\u2026\u201d and belong to no set. Another set is a " +
                    "folder, five Addressable entries under a new prefix, and one line in each array " +
                    "in SpriteManager.",
            },
        ],
        options: [
            {
                label: "Current Set", code: "SliceArtSet", kind: "readonly", default: "Mesh tight seams",
                figure: "slice",
                summary: "The art set in force, by name. Read-only \u2014 the buttons below change it.",
                detail: "Shown as text rather than left to be inferred from which button was pressed last, " +
                    "because the panel is read rather than remembered and it survives a domain reload " +
                    "where the memory of pressing a button does not.",
            },
            {
                label: "Use Mesh tight seams", code: "SliceArtUseMeshTight", kind: "action",
                figure: "slice",
                summary: "The DEFAULT: the mesh with every seam pulled in \u2014 a tighter, denser ring.",
                detail: "The same geometry as set 0, re-rendered with ring gap 0.008, wedge gap " +
                    "0.008 and corner radius 0.021, against 0.010, 0.015 and 0.025. The stages sit " +
                    "closer together, the wedges sit closer side to side, and the corners are very " +
                    "slightly sharper. Stages 3 and 4 come out a pixel LARGER than in set 0, because a " +
                    "narrower ring gap leaves the inner bands more room.",
                tip: "This is both what a fresh install wears and what the generator now produces: " +
                    "art-gen/slice-mesh.html carries these three values, so an export run today " +
                    "lands here. A device that has ever pressed one of these buttons keeps its " +
                    "own stored choice and will not follow a change of default.",
            },
            {
                label: "Next Set", code: "SliceArtNextSet", kind: "action",
                figure: "slice",
                summary: "Step to the next set, wrapping past the last one back to the shipped one.",
                detail: "One button cycles however many sets there are, so it keeps working whatever gets " +
                    "added. With a single set in the project it lands back on that set \u2014 harmless, " +
                    "and it starts doing something again the moment a second one appears.",
                tip: "The swap lands on the slices already on screen, not just on ones dealt " +
                    "afterwards, so you can A/B a live level without restarting it.",
                warn: "It re-applies the stage each slice was last SET to. Pressed in the middle of a " +
                    "shuffle collapse \u2014 where a slice is deliberately showing another stage's " +
                    "art while it travels \u2014 a slice can settle a moment early. It corrects " +
                    "itself on the next stage change.",
            },
        ],
    },
    {
        id: "slices",
        label: "Slices",
        blurb: "The coloured wedges around the ring. A ball riding past a wedge of its own colour hops in; fill a " +
            "wedge and it clears. A wedge is treated as ONE target, not as separate holes.",
        fixed: [
            {
                label: "HolesPerSlice", value: "3",
                note: "How many balls a wedge takes before it's full. Fixed in code (SlicesOptions.HolesPerSlice). " +
                    "Balls always fill hole 1, then 2, then 3 — never out of order.",
            },
        ],
        options: [
            {
                label: "Enter Allowance (deg)", code: "SliceEnterAllowance", kind: "float", default: "15", unit: "degrees",
                figure: "slice",
                summary: "How far a ball has to travel INTO a wedge before it's allowed to hop in.",
                detail: "Each wedge owns a quarter of the belt — 90 degrees. A matching ball anywhere in that arc " +
                    "can hop in, and these two values trim the arc at each end.\n\n" +
                    "This one trims the entering end. At 0 a ball hops the instant it arrives over the wedge; " +
                    "at 20 it has to be 20 degrees in first.",
                tip: "Shipped at 15 in, 0 out — a ball waits a sixth of the wedge before it may hop, then stays eligible right to the far edge (75 of the 90 degrees). Much lower and the first screw hops FORWARD to the slice's first hole (at ~19 degrees).",
                warn: "Bigger means a NARROWER window, not a wider one. Both values are trims inward.",
            },
            {
                label: "Arrival Early Enter (deg)", code: "SliceEarlyEnterAllowance", kind: "float", default: "10", unit: "degrees",
                summary: "An earlier window start the screws filling a just-arrived slice may hop from.",
                detail: "Part of the arrival rules, decided the moment a slice arrives at the top of its wedge: if the " +
                    "normal window (Enter Allowance) holds fewer matching screws than the slice has free holes, " +
                    "but the window started here holds enough, those screws may hop from here, so they go in " +
                    "together instead of the last one dribbling in. Only those screws; everything else still " +
                    "waits for Enter Allowance.",
                warn: "A TRIM like Enter Allowance, so smaller = earlier. At or above Enter Allowance it never applies.",
            },
            {
                label: "Exit Allowance (deg)", code: "SliceExitAllowance", kind: "float", default: "0", unit: "degrees",
                summary: "How far before the END of the wedge a ball stops being allowed to hop in.",
                detail: "Trims the leaving end of the same arc. At 0 a ball can hop right up to the moment it " +
                    "leaves the wedge; at 20 it loses the chance 20 degrees early and goes round again.",
                tip: "Raise this if balls are hopping in so late that the jump looks like it happens sideways or behind the wedge.",
            },
            {
                label: "Jump Peak Duration", code: "SliceJumpPeakDuration", kind: "float", default: "0.12", unit: "seconds",
                summary: "How long after take-off the hop reaches its peak size.",
                detail: "The swell eases in on a SmoothStep — curved at both ends, so the ball leaves the belt " +
                    "growing gently instead of snapping outward on the first frame — and then HOLDS at the peak " +
                    "for whatever is left of the flight, arriving at a size that is already still. Clamped into the " +
                    "flight: at or past the Hop Duration (0.18s) the swell runs the whole way over, and 0 starts the " +
                    "ball at the peak.",
                tip: "The default puts the peak two thirds of the way over, past the top of the arc, so the ball is still growing as it comes down.",
            },
            {
                label: "Land Settle Duration", code: "SliceLandSettleDuration", kind: "float", default: "0.12", unit: "seconds",
                summary: "How long the seat takes — the press from the flight's peak down into the hole.",
                detail: "The ball keeps its full belt size for the entire flight, and only resizes once it has landed — " +
                    "so this is the landing punch, not the hop.",
                tip: "Keep it short. Stretch it out and the ball looks like it's deflating rather than snapping in.",
            },
            {
                label: "Step Duration", code: "SliceStepDuration", kind: "float", default: "0.15", unit: "seconds",
                summary: "How long one stack step takes when a wedge fills and clears.",
                detail: "When a slice fills, the whole stack behind it grows up into its new positions — one " +
                    "motion, and this is its length (the filled slice's own exit is the Complete Rise, below). " +
                    "The new active slice opens for balls almost immediately (it only has to be ready before " +
                    "a ball's hop lands), so this is about how the clear FEELS, not how long the wedge is shut.",
                tip: "Keep it below the Hole Jump Duration (0.18) — the new slice counts on arriving before a launched ball does.",
            },
            {
                label: "Step Stagger", code: "SliceStepStagger", kind: "float", default: "0.05", unit: "seconds",
                summary: "How long each deeper slice waits before following the one in front of it up the stack.",
                detail: "When a wedge clears, the slice stepping into the gap starts growing at once; the one behind it " +
                    "starts this much later, the next one this much later again — so the stack ripples inward " +
                    "instead of moving as one block. Each grow still takes the Step Duration. The new active " +
                    "slice is always the first to move, so a ball's hop never waits on the ripple.",
                tip: "0 restores the single block step. Around a third of the Step Duration reads as a ripple; " +
                    "past that the back of the stack visibly lags.",
            },
            {
                label: "Land Bump Distance", code: "SliceLandBumpDistance", kind: "float", default: "0.05", unit: "world units",
                summary: "How far a slice dips when a screw lands in it, before springing back.",
                detail: "The slice's art, its holes and the screws already seated in it all dip together. A screw landing while a dip is already playing doesn't start another, so quick landings read as one bob.",
                tip: "0 turns the dip off.",
            },
            {
                label: "Land Bump Duration", code: "SliceLandBumpDuration", kind: "float", default: "0.14", unit: "seconds",
                summary: "How long a slice's landing dip takes, down and back.",
            },
            {
                label: "Exit Duration", code: "SliceExitDuration", kind: "float", default: "0.2", unit: "seconds",
                summary: "How long a completed slice takes to be pressed flat into the ground and fade away.",
                detail: "It goes down by its own thickness while its walls shorten by as much, so it ends flat on the ground. " +
                    "The slices behind it step up into the gap at the same time; they don't wait for the exit.",
            },
            {
                label: "Exit Fade Start", code: "SliceExitFadeStart", kind: "float", default: "0.4",
                summary: "How far through the exit the completed slice starts to fade (0.4 = solid for the first 40%, then fades out over the rest).",
                tip: "0 fades it across the whole sink; closer to 1 makes the fade a quicker snuff-out at the end.",
            },
            {
                label: "Wedge Cover Slide Duration", code: "WedgeCoverSlideDuration", kind: "float", default: "0.4", unit: "seconds",
                summary: "How long a finished wedge's cover takes to slide into place.",
                detail: "Once a wedge's last slice has finished its exit, a cover closes over it like a window shutter: it slides " +
                    "out from the ring centre along the wedge's 45° diagonal to its resting place, clipped to the " +
                    "wedge so nothing shows outside it. This is the length of that slide.",
            },
            {
                label: "Wedge Cover Slide Delay", code: "WedgeCoverSlideDelay", kind: "float", default: "0", unit: "seconds",
                summary: "How long a finished wedge waits before its cover starts sliding in.",
                detail: "Counted from the moment the wedge's last slice starts its exit: at 0 the cover starts sliding " +
                    "in at once, over the slice still sinking away (the cover draws above every slice).",
            },
            {
                label: "Wedge Cover Slide Ease", code: "WedgeCoverSlideEase", kind: "enum", default: "InQuint",
                summary: "The easing curve of the cover's slide.",
                detail: "A dropdown of DOTween easing curves. InQuint (the default) holds the cover back and then slams " +
                    "it home hard; \"Out\" curves (e.g. OutCubic) shoot it out fast and settle it into place; OutBack overshoots and " +
                    "springs back — the overshoot runs past the wedge's edge, where the clip hides it.",
            },
            {
                label: "Wedge Sparkle Count", code: "WedgeSparkleCount", kind: "int", default: "16",
                summary: "How many star glints a finished wedge throws off its rim once its cover has slid home.",
                detail: "The moment the cover settles, glints and a few smaller dots (about 0.6x this many) puff out " +
                    "from the wedge's curved rim, spread along the whole arc, and billow outward over the belt like " +
                    "dust — each on its own bearing and speed, wavering as it goes (see Spread and Turbulence). " +
                    "0 turns the puff off.",
            },
            {
                label: "Wedge Sparkle Distance", code: "WedgeSparkleDistance", kind: "float", default: "0.7", unit: "world units",
                summary: "How far the sparkles drift outward from the rim before they stop.",
                detail: "They leave the rim and brake without quite stopping; each travels 25-125% of this. " +
                    "The dots go about a quarter further than the glints. The belt starts right at the rim, so " +
                    "around 0.6-0.9 puts them across it.",
            },
            {
                label: "Wedge Sparkle Spread", code: "WedgeSparkleSpread", kind: "float", default: "110", unit: "degrees",
                summary: "How wide the cone of directions the sparkles leave the rim in.",
                detail: "Centred on straight outward from the ring centre. 0 sends every one out radially (the old " +
                    "starburst); 110 lets them fan out, most still leaving the rim, a few skimming along it; 180 is " +
                    "anything from along the rim one way to the other.",
            },
            {
                label: "Wedge Sparkle Turbulence", code: "WedgeSparkleTurbulence", kind: "float", default: "0.8",
                summary: "How much the sparkles waver as they drift — the dusty wander.",
                detail: "A scrolling noise nudges each one off its line; the dots get a bit more of it than the glints. " +
                    "0 = straight lines.",
            },
            {
                label: "Wedge Sparkle Lifetime", code: "WedgeSparkleLifetime", kind: "float", default: "0.55", unit: "seconds",
                summary: "How long each sparkle lives (each gets 75-115% of this).",
                detail: "They pop up to full size in the first moment, then shrink away, fading over the second half.",
            },
            {
                label: "Wedge Sparkle Size", code: "WedgeSparkleSize", kind: "float", default: "0.45", unit: "world units",
                summary: "A star glint's size at its peak (each is 70-120% of this; the dots are under half).",
            },
            {
                label: "Wedge Cover Slide Start", code: "WedgeCoverSlideStart", kind: "float", default: "0",
                summary: "How much of the cover already shows when the slide starts (0-1).",
                detail: "0 = fully hidden: the cover starts a whole ring radius back, behind the centre, and the " +
                    "entire panel slides in. 0.5 starts it halfway in, so only the outer half travels; 1 = no " +
                    "slide, the cover just appears.",
            },
            {
                label: "Ice Shake", code: "SliceIceShake", kind: "float", default: "1",
                figure: "slice",
                summary: "How much a frozen slice's ice jolts when it's hit (0 = it holds still, 1 = the full shake).",
                detail: "The first hit punches the ice's scale - a quick wobble, up to 4% at 1 - and the breaking hit " +
                    "swells it, 6% at 1, as it fades. The ice scales about its own centre (the middle of its " +
                    "band), so it swells and shrinks in place. Both are multiplied by this.",
            },
        ],
    },
    // -----------------------------------------------------------------------
    {
        id: "sr-categories",
        label: "SR Categories",
        blurb: "The panel's last tab, from core (SRDebugCategoriesOptions): which of the tabs above it the panel shows. " +
            "Untick what you don't need while tuning, then Reload SR.",
        options: [
            {
                label: "Reload SR", code: "SRDebugCategoriesOptions.Reload", kind: "action",
                summary: "Lays the panel out again, leaving out every tab whose box below is unticked.",
                detail: "Until you press it, ticking and unticking only records the choice. It also rebuilds the " +
                    "checkbox list, so a tab added since the panel opened gets its box.",
            },
            {
                label: "Reset Selections", code: "SRDebugCategoriesOptions.ResetSelections", kind: "action",
                summary: "Puts every box back at its default and lays the panel out that way at once.",
                detail: "Forgets the kept box states (PlayerPrefs SRDebug.CategoryChoices), so the device follows the " +
                    "[SRCategory] defaults again, including ones changed in code since the states were kept.",
            },
            {
                label: "Detail Level", code: "SRDebugCategoriesOptions.DetailLevel", kind: "enum", default: "Crucial",
                summary: "How deep the panel goes: Crucial shows only crucial rows, Moderate adds moderate ones, Extra shows all.",
                detail: "Each row's level is its [SRLevel] (shown on this page next to its type); a row without one " +
                    "counts as Crucial. Applies with Reload SR together with the tab boxes, so a row shows only when " +
                    "both its tab and its level let it. Kept on the device once changed (PlayerPrefs " +
                    "SRDebug.DetailLevel); Reset Selections puts it back to Crucial.",
            },
            {
                label: "(one per tab, named after it)", code: "SRCategory.EnabledByDefault", kind: "bool",
                default: "on for Nexus, General, Harbor, Ads, Data, Remote Config, Connection, Gameplay, Inventory; off for the rest",
                summary: "Unticked = that tab is left out of the panel after the next Reload SR.",
                detail: "One box per tab, in the order the tabs come. Each starts as its class's [SRCategory] says " +
                    "(EnabledByDefault). A tab with rows from both core and the game (General, Haptics) is one box " +
                    "and goes as one. Hiding a tab only leaves it out of the panel: its options keep their values " +
                    "and keep working. Once you change a box, every box's state is kept on the device, so the next " +
                    "launch comes up exactly as you left it (PlayerPrefs SRDebug.CategoryChoices) until Reset " +
                    "Selections; a tab added later starts at its default. This tab itself can't be hidden.",
            },
        ],
    },
];
/** Total documented rows, shown in the page header as a nudge to keep this file honest. */
export const OPTION_COUNT = SR_CATEGORIES.reduce((n, c) => n + c.options.length, 0);
